import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  Image,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
  Animated,
  Easing,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import AdminScreen from './screens/AdminScreen';
import ContactScreen from './screens/ContactScreen';
import InvoicePayScreen from './screens/InvoicePayScreen';
import { auth, db, functions } from './config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, addDoc, serverTimestamp, query, where, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Firebase callable functions
const sendBookingConfirmation = httpsCallable(functions, 'sendBookingConfirmation');
const sendEstimateEmail = httpsCallable(functions, 'sendEstimateEmail');
const generateEstimateCallable = httpsCallable(functions, 'generateEstimate');

// Auth Context for global user state
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Check if user is admin in Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists() && userDoc.data().userType === 'admin') {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.log('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    // Also check localStorage for admin session (from admin panel login)
    if (isWeb && typeof window !== 'undefined') {
      const adminLoggedIn = localStorage.getItem('handyhawk_admin_logged_in');
      if (adminLoggedIn === 'true') {
        setIsAdmin(true);
      }
    }

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const { width, height } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Simple URL-based router for web
const getInitialPath = () => {
  if (isWeb && typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '/';
};

const useRouter = () => {
  const [path, setPath] = useState(getInitialPath);

  useEffect(() => {
    if (isWeb && typeof window !== 'undefined') {
      // Update path on mount in case initial state was wrong
      setPath(window.location.pathname);
      
      const handlePopState = () => {
        setPath(window.location.pathname);
      };
      
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, []);

  // Update document title based on current path
  useEffect(() => {
    if (isWeb && typeof document !== 'undefined') {
      const pageTitles = {
        '/': 'Home',
        '/contact': 'Contact Us',
        '/admin': 'Admin',
        '/services': 'Services',
        '/about': 'About Us',
        '/portfolio': 'Portfolio',
        '/estimate': 'Get Estimate',
        '/checkout': 'Checkout',
      };
      
      // Handle dynamic routes
      let pageTitle = pageTitles[path];
      if (!pageTitle) {
        if (path.startsWith('/pay/')) {
          pageTitle = 'Pay Invoice';
        } else if (path.startsWith('/checkout/')) {
          pageTitle = 'Checkout';
        } else {
          pageTitle = 'Home';
        }
      }
      document.title = `Handy Hawk | ${pageTitle}`;
    }
  }, [path]);

  const navigate = (newPath) => {
    if (isWeb && typeof window !== 'undefined') {
      window.history.pushState({}, '', newPath);
      setPath(newPath);
    }
  };

  return { path, navigate };
};

// HandyHawk Brand Colors - Enhanced
// SWAP: To revert to teal, change accent colors back to teal values
const colors = {
  darkNavy: '#0a0e14',
  navy: '#12171f',
  navyLight: '#1a2332',
  navyMid: '#252f3f',
  // Primary accent - GOLD (from logo)
  teal: '#d4a84b',           // Was: '#00d9a5'
  tealDark: '#c9975a',       // Was: '#00b386'
  tealGlow: 'rgba(212, 168, 75, 0.15)',  // Was: 'rgba(0, 217, 165, 0.15)'
  tealBright: '#f0c865',     // Was: '#00ffbe'
  // Secondary accent
  gold: '#40b4b4',           // Swapped - now teal for secondary
  goldLight: '#4ecdc4',
  white: '#ffffff',
  cream: '#f8fafc',
  gray: '#6b7a90',
  grayLight: '#94a3b8',
  grayDark: '#3d4a5c',
  accent: '#d4a84b',         // Was: '#00d9a5'
  glass: 'rgba(18, 23, 31, 0.8)',
  glassBorder: 'rgba(255, 255, 255, 0.08)',
};

// Default Services Data (fallback if Firestore is empty)
const defaultServices = [
  { 
    icon: '🔧', iconName: 'tool', title: 'General Repairs', 
    desc: 'Fixing what\'s broken around your home', color: colors.teal,
    detailedDesc: 'Our general repair services cover everything from squeaky doors to broken fixtures. We handle minor repairs quickly and efficiently, ensuring your home stays in top shape.',
    autoTypeText: 'I need help with some general repairs around my home. '
  },
  { 
    icon: '🔨', iconName: 'hammer', title: 'Carpentry', 
    desc: 'Custom woodwork and installations', color: '#ef4444',
    detailedDesc: 'From custom shelving to trim work and deck repairs, our carpentry services bring craftsmanship to every project. We work with all types of wood and can match existing styles.',
    autoTypeText: 'I need carpentry work done. '
  },
  { 
    icon: '🪟', iconName: 'layout', title: 'Doors & Windows', 
    desc: 'Installation and repair services', color: '#3b82f6',
    detailedDesc: 'We install and repair interior and exterior doors, windows, and screens. Whether it\'s a sticky door or a drafty window, we\'ll get it working perfectly.',
    autoTypeText: 'I need help with my doors or windows. '
  },
  { 
    icon: '🎨', iconName: 'edit-2', title: 'Painting', 
    desc: 'Interior and exterior painting', color: '#a855f7',
    detailedDesc: 'Transform your space with professional painting services. We handle prep work, priming, and painting with attention to detail. Interior and exterior projects welcome.',
    autoTypeText: 'I need painting services for my home. '
  },
  { 
    icon: '⚡', iconName: 'zap', title: 'Electrical', 
    desc: 'Light fixtures and outlet repairs', color: '#f59e0b',
    detailedDesc: 'Safe, reliable electrical work including light fixture installation, outlet repairs, switch replacements, and ceiling fan installation. Licensed and insured.',
    autoTypeText: 'I have an electrical problem that needs attention. '
  },
  { 
    icon: '🚿', iconName: 'droplet', title: 'Plumbing', 
    desc: 'Leak repairs and installations', color: '#06b6d4',
    detailedDesc: 'From leaky faucets to clogged drains, we handle all your plumbing needs. Toilet repairs, water heater maintenance, and pipe work done right.',
    autoTypeText: 'I need help with plumbing issues. '
  },
  { 
    icon: '🏗️', iconName: 'layers', title: 'Renovations', 
    desc: 'Transform your living spaces', color: '#f97316',
    detailedDesc: 'Ready for a change? We handle bathroom updates, kitchen refreshes, and room transformations. Let us help you reimagine your space.',
    autoTypeText: 'I am looking to renovate part of my home. '
  },
  { 
    icon: '🏠', iconName: 'home', title: 'Roofing', 
    desc: 'Roof repairs and maintenance', color: '#64748b',
    detailedDesc: 'Protect your home with professional roof repairs. We handle shingle replacement, leak detection, gutter cleaning, and preventive maintenance.',
    autoTypeText: 'I need roofing repairs or inspection. '
  },
];

// Icon name to emoji mapping for displaying services
const iconToEmoji = {
  'tool': '🔧',
  'hammer': '🔨',
  'layout': '🪟',
  'edit-2': '🎨',
  'zap': '⚡',
  'droplet': '🚿',
  'layers': '🏗️',
  'home': '🏠',
  'wrench': '🔧',
  'settings': '⚙️',
  'truck': '🚚',
  'shield': '🛡️',
};

// Portfolio Items
const portfolioItems = [
  { id: 1, type: 'image', title: 'Kitchen Renovation', category: 'Renovation', color: '#ef4444' },
  { id: 2, type: 'video', title: 'Deck Building Process', category: 'Carpentry', color: '#3b82f6' },
  { id: 3, type: 'image', title: 'Bathroom Remodel', category: 'Plumbing', color: '#06b6d4' },
  { id: 4, type: 'image', title: 'Custom Cabinets', category: 'Carpentry', color: '#a855f7' },
  { id: 5, type: 'video', title: 'Full Home Repair', category: 'General', color: '#f59e0b' },
  { id: 6, type: 'image', title: 'Exterior Painting', category: 'Painting', color: '#f97316' },
];

// Testimonials - All real reviews from Google
const testimonials = [
  {
    name: 'Dani Michales',
    location: 'Las Vegas, NV',
    text: 'Excellent Service! Work was perfect, completed in a timely manner. We had a room painted with an accent wall and it turned out wonderfully. Great price! Especially with contractors overcharging due to inflation. Thanks for a great job HandyHawk!',
    rating: 5,
  },
  {
    name: 'Mkkkgavnari Lane',
    location: 'Las Vegas, NV',
    text: 'Amazing work! Super affordable, Jesse was great! Super helpful & knowledgeable. Will be keeping his contact for any of my next handy needs!!!! Thank you!!!!!',
    rating: 5,
  },
  {
    name: 'James Carter Jr',
    location: 'Las Vegas, NV',
    text: 'Jesse did an awesome job for us installing cabinets in our pantry. He was on time and gave an excellent quote. We will be calling him first if we have additional work.',
    rating: 5,
  },
  {
    name: 'Janet Ruiz',
    location: 'Las Vegas, NV',
    text: 'Jesse is very professional. Arrived on time. He did a great job installing our new garage door opener. He is very friendly and easy to talk to. Did not feel any awkwardness while he was working.',
    rating: 5,
  },
  {
    name: 'Grace B',
    location: 'Las Vegas, NV',
    text: 'I highly recommend this business. Jesse was very professional. He arrived on time, always keeps you updated when working on a project, and provided quality work. From painting, to installing, to staining, etc. He knows his craft.',
    rating: 5,
  },
  {
    name: 'Crystal Franklin-Barker',
    location: 'Henderson, NV',
    text: 'I had a plumbing issue on a Saturday. Jesse provided a work-around until he could get to me on Sunday. He was prompt, knowledgeable, had all the needed tools and parts, and completed the work accurately and efficiently. Highly recommend!',
    rating: 5,
  },
  {
    name: 'Irina Rajala',
    location: 'Las Vegas, NV',
    text: 'When things totally fell apart to the point that I had no running water in the house I called Handy Hawk. Jesse came in at 9am next day and completed all work by noon. He fixed and replaced multiple issues.',
    rating: 5,
  },
  {
    name: 'AgentRood',
    location: 'Las Vegas, NV',
    text: 'Jesse came out to take a look at my water heater. He not only made the time to get here today, but when he found the spark wasn\'t reaching the pilot, instead of declaring the need for replacement, he fixed it on the spot!',
    rating: 5,
  },
  {
    name: 'Evian',
    location: 'Las Vegas, NV',
    text: 'Jesse was very cool! Coolest handyman that I\'ve met and he talked to my whole family and he did amazing work and was priced cheap. A+',
    rating: 5,
  },
  {
    name: 'Robert Carter',
    location: 'Las Vegas, NV',
    text: 'I had 2 (4x8) garage shelving units installed. Work was completed with 100% satisfaction. I plan to use this company in the future. Use this company if you want the job completed professionally.',
    rating: 5,
  },
  {
    name: 'Anthony Powell',
    location: 'Henderson, NV',
    text: 'Mr. Jessie was very kind and considerate. I appreciated his professional demeanor. A job well done!',
    rating: 5,
  },
  {
    name: 'Joseph DiStephano',
    location: 'Las Vegas, NV',
    text: 'Jessie and his co-worker were very professional. We appreciate you fixing our chandelier, you\'re the best. The price was reasonable as well.',
    rating: 5,
  },
  {
    name: 'Lil Butterfly',
    location: 'Las Vegas, NV',
    text: 'Once again HandyHawk has saved the day. My garbage disposal stopped working & I couldn\'t fix it myself. I sent one text message to HandyHawk & now I have a brand new one. I recommend him to everyone!',
    rating: 5,
  },
  {
    name: 'Venette Mahan',
    location: 'Las Vegas, NV',
    text: 'Great service!!!! Jesse arrived timely, communicated with me. Was respectful of the property. Found me a great handyman!!!',
    rating: 5,
  },
  {
    name: 'Darnell Buchanan',
    location: 'Henderson, NV',
    text: 'Very respectful, easy to work with on a job I needed done which was a new board under my sink in the kitchen. Thank you again for your help.',
    rating: 5,
  },
];

// Animated Section with Intersection Observer-like behavior
const AnimatedSection = ({ children, delay = 0, style, nativeID, id, ...props }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 1000,
          delay,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: !isWeb,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 1000,
          delay,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: !isWeb,
        }),
      ]).start();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Animated.View
      nativeID={nativeID}
      {...(isWeb && id ? { id } : {})}
      style={[
        style,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      {...props}
    >
      {children}
    </Animated.View>
  );
};

// Scroll to section helper
const scrollToSection = (sectionId) => {
  if (isWeb && typeof document !== 'undefined') {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    // If scrolling to hero, dispatch an event to activate the estimate form
    if (sectionId === 'hero') {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('activateEstimateForm'));
      }, 400); // Small delay to allow scroll to complete
    }
  }
};

// Navigate helper
const navigateTo = (path) => {
  if (isWeb && typeof window !== 'undefined') {
    window.location.href = path;
  }
};

// Inject CSS for shimmer animation (web only)
if (isWeb && typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .shimmer-btn {
      position: relative;
      overflow: hidden;
    }
    .shimmer-btn::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg, 
        transparent 0%, 
        rgba(255,255,255,0.15) 50%, 
        transparent 100%
      );
      animation: shimmer 2.5s ease-in-out infinite;
      pointer-events: none;
    }
    .hours-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(212, 168, 75, 0.12);
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      color: #d4a84b;
    }
  `;
  document.head.appendChild(style);
}

// ShimmerButton component
const ShimmerButton = ({ children, style, onPress, disabled }) => {
  const shimmerAnim = useRef(new Animated.Value(-1)).current;

  useEffect(() => {
    if (!isWeb) {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(shimmerAnim, {
            toValue: 1,
            duration: 2500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(shimmerAnim, {
            toValue: -1,
            duration: 0,
            useNativeDriver: true,
          }),
        ])
      );
      loop.start();
      return () => loop.stop();
    }
  }, []);

  return (
    <TouchableOpacity 
      style={[style, { overflow: 'hidden' }]} 
      onPress={onPress}
      disabled={disabled}
      className={isWeb ? 'shimmer-btn' : undefined}
    >
      {children}
      {!isWeb && (
        <Animated.View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: [{
              translateX: shimmerAnim.interpolate({
                inputRange: [-1, 1],
                outputRange: [-width, width],
              }),
            }],
          }}
        >
          <View
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
            }}
          />
        </Animated.View>
      )}
    </TouchableOpacity>
  );
};

// Full Screen Mobile Menu Overlay
const MobileMenuOverlay = ({ isOpen, onClose, navItems, isAdmin, onNavClick, navigateTo, scrollToSection, todayHoursText }) => {
  const slideAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    if (isOpen) {
      Animated.spring(slideAnim, {
        toValue: 1,
        tension: 65,
        friction: 11,
        useNativeDriver: !isWeb,
      }).start();
    }
  }, [isOpen]);
  
  const handleClose = () => {
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: !isWeb,
    }).start(() => onClose());
  };
  
  const handleItemPress = (item) => {
    handleClose();
    setTimeout(() => onNavClick(item), 150);
  };
  
  const handleAdminPress = () => {
    handleClose();
    setTimeout(() => navigateTo('/admin'), 150);
  };
  
  const handleCTAPress = () => {
    handleClose();
    setTimeout(() => scrollToSection('hero'), 150);
  };
  
  const overlayOpacity = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });
  
  const menuSlideX = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [width, 0],
  });
  
  return (
    <View style={styles.mobileOverlayContainer}>
      {/* Backdrop */}
      <Animated.View 
        style={[styles.mobileOverlayBackdrop, { opacity: overlayOpacity }]}
      >
        <TouchableOpacity 
          style={{ flex: 1 }} 
          onPress={handleClose} 
          activeOpacity={1}
        />
      </Animated.View>
      
      {/* Menu Panel */}
      <Animated.View 
        style={[
          styles.mobileOverlayPanel,
          isWeb ? { transform: [{ translateX: menuSlideX }] } : { right: menuSlideX }
        ]}
      >
        {/* Close Button */}
        <TouchableOpacity style={styles.mobileOverlayClose} onPress={handleClose}>
          <View style={styles.mobileOverlayCloseX}>
            <View style={[styles.mobileOverlayCloseLine, { transform: [{ rotate: '45deg' }] }]} />
            <View style={[styles.mobileOverlayCloseLine, { transform: [{ rotate: '-45deg' }] }]} />
          </View>
        </TouchableOpacity>
        
        {/* Scrollable Content */}
        <ScrollView 
          style={styles.mobileOverlayScroll}
          contentContainerStyle={styles.mobileOverlayScrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Logo */}
          <View style={styles.mobileOverlayLogo}>
            <Text style={styles.mobileOverlayLogoText}>
              <Text style={{ color: colors.teal }}>HANDY</Text>
              <Text style={{ color: colors.white }}>HAWK</Text>
            </Text>
            <View style={styles.mobileOverlayLogoBar} />
          </View>
          
          {/* Menu Items */}
          <View style={styles.mobileOverlayNav}>
            {navItems.map((item, index) => (
              <TouchableOpacity 
                key={index}
                style={styles.mobileOverlayItem}
                onPress={() => handleItemPress(item)}
                activeOpacity={0.7}
              >
                <Text style={styles.mobileOverlayItemArrow}>→</Text>
                <Text style={styles.mobileOverlayItemText}>{item.label}</Text>
                <Text style={styles.mobileOverlayItemArrowEnd}>→</Text>
              </TouchableOpacity>
            ))}
            
            {isAdmin && (
              <TouchableOpacity 
                style={[styles.mobileOverlayItem, styles.mobileOverlayItemAdmin]}
                onPress={handleAdminPress}
                activeOpacity={0.7}
              >
                <Text style={[styles.mobileOverlayItemArrow, { color: colors.teal }]}>→</Text>
                <Text style={[styles.mobileOverlayItemText, { color: colors.teal }]}>Admin Dashboard</Text>
                <Text style={[styles.mobileOverlayItemArrowEnd, { color: colors.teal }]}>→</Text>
              </TouchableOpacity>
            )}
          </View>
          
          {/* CTA Button */}
          <View style={styles.mobileOverlayCTAContainer}>
            <TouchableOpacity 
              style={styles.mobileOverlayCTA}
              onPress={handleCTAPress}
              activeOpacity={0.8}
            >
              <Text style={styles.mobileOverlayCTAText}>Get Free Estimate</Text>
              <Text style={styles.mobileOverlayCTAArrow}>→</Text>
            </TouchableOpacity>
          </View>
          
          {/* Footer Info */}
          <View style={styles.mobileOverlayFooter}>
            <TouchableOpacity 
              style={styles.mobileOverlayPhoneBtn}
              onPress={() => isWeb && window.open('tel:+17029369959', '_self')}
            >
              <Text style={styles.mobileOverlayPhone}>(702) 936-9959</Text>
            </TouchableOpacity>
            <Text style={styles.mobileOverlayHours}>Today: {todayHoursText || '8AM - 5PM'}</Text>
            <Text style={styles.mobileOverlaySocialText}>LICENSED • INSURED • TRUSTED</Text>
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );
};

// Floating Glass Navbar
const Navbar = ({ scrollY, scrollViewRef }) => {
  const { isAdmin } = useAuth() || { isAdmin: false };
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [todayHours, setTodayHours] = useState('8AM - 5PM');
  const [isCurrentlyOpen, setIsCurrentlyOpen] = useState(false);
  const [hoursDropdownOpen, setHoursDropdownOpen] = useState(false);
  const [weekHours, setWeekHours] = useState([]);
  const translateY = useRef(new Animated.Value(0)).current;
  const bgOpacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(1)).current;
  const lastScrollY = useRef(0);
  const isHidden = useRef(false);

  // Format time helper
  const formatTime = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
  };

  // Check if currently within business hours (PST)
  const checkIfOpen = (todayData) => {
    if (!todayData || !todayData.isOpen) return false;
    
    // Get current time in PST
    const now = new Date();
    const pstTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const currentMinutes = pstTime.getHours() * 60 + pstTime.getMinutes();
    
    // Parse open/close times
    const [openH, openM] = todayData.open.split(':').map(Number);
    const [closeH, closeM] = todayData.close.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };

  // Default business hours (Mon-Sat 8AM-5PM PST, Sunday closed)
  const getDefaultHours = () => ({
    sunday: { isOpen: false, open: '08:00', close: '17:00' },
    monday: { isOpen: true, open: '08:00', close: '17:00' },
    tuesday: { isOpen: true, open: '08:00', close: '17:00' },
    wednesday: { isOpen: true, open: '08:00', close: '17:00' },
    thursday: { isOpen: true, open: '08:00', close: '17:00' },
    friday: { isOpen: true, open: '08:00', close: '17:00' },
    saturday: { isOpen: true, open: '08:00', close: '17:00' },
  });

  // Fetch business hours (with date overrides support)
  useEffect(() => {
    const fetchHours = async () => {
      try {
        const hoursDoc = await getDoc(doc(db, 'settings', 'hours'));
        const hoursData = hoursDoc.exists() ? hoursDoc.data() : getDefaultHours();
        
        // Extract dateOverrides from the hours document
        const dateOverrides = hoursData.dateOverrides || {};
        
        // Get the regular weekly hours (exclude dateOverrides from this object)
        const { dateOverrides: _, ...hours } = hoursData;
        
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        // Get today in PST
        const now = new Date();
        const pstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        const todayIndex = pstNow.getDay();
        const today = dayNames[todayIndex];
        
        // Format today's date as YYYY-MM-DD for override lookup
        const todayDateStr = pstNow.toISOString().split('T')[0];
        
        // Check if there's a date override for today
        const todayOverride = dateOverrides[todayDateStr];
        let todayData;
        
        if (todayOverride) {
          // Use the date override
          if (todayOverride.closed) {
            todayData = { isOpen: false, reason: todayOverride.reason };
          } else {
            todayData = { 
              isOpen: true, 
              open: todayOverride.open || '08:00', 
              close: todayOverride.close || '17:00' 
            };
          }
        } else {
          // Use regular weekly hours
          todayData = hours[today] || { isOpen: false };
        }
        
        // Set today's hours display
        if (todayData && todayData.isOpen) {
          setTodayHours(`${formatTime(todayData.open)} - ${formatTime(todayData.close)}`);
          setIsCurrentlyOpen(checkIfOpen(todayData));
        } else {
          setTodayHours(todayData?.reason ? `Closed - ${todayData.reason}` : 'Closed');
          setIsCurrentlyOpen(false);
        }
        
        // Build next 7 days hours array
        const weekData = [];
        for (let i = 0; i < 7; i++) {
          const futureDate = new Date(pstNow);
          futureDate.setDate(pstNow.getDate() + i);
          const futureDateStr = futureDate.toISOString().split('T')[0];
          const dayIndex = futureDate.getDay();
          const dayName = dayNames[dayIndex];
          
          // Check for date override first
          const dayOverride = dateOverrides[futureDateStr];
          let dayData;
          
          if (dayOverride) {
            if (dayOverride.closed) {
              dayData = { isOpen: false, reason: dayOverride.reason };
            } else {
              dayData = { 
                isOpen: true, 
                open: dayOverride.open || '08:00', 
                close: dayOverride.close || '17:00' 
              };
            }
          } else {
            dayData = hours[dayName] || { isOpen: false };
          }
          
          let hoursText;
          if (dayData?.isOpen) {
            hoursText = `${formatTime(dayData.open)} - ${formatTime(dayData.close)}`;
          } else {
            hoursText = dayData?.reason || 'Closed';
          }
          
          weekData.push({
            label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayLabels[dayIndex],
            fullDay: dayNames[dayIndex].charAt(0).toUpperCase() + dayNames[dayIndex].slice(1),
            isOpen: dayData?.isOpen || false,
            hours: hoursText,
            isToday: i === 0,
            hasOverride: !!dayOverride,
          });
        }
        setWeekHours(weekData);
      } catch (error) {
        console.log('Error fetching hours, using defaults:', error);
        // Use defaults on error
        const defaults = getDefaultHours();
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const now = new Date();
        const pstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        const todayIndex = pstNow.getDay();
        const today = dayNames[todayIndex];
        const todayData = defaults[today];
        
        if (todayData && todayData.isOpen) {
          setTodayHours(`${formatTime(todayData.open)} - ${formatTime(todayData.close)}`);
          setIsCurrentlyOpen(checkIfOpen(todayData));
        } else {
          setTodayHours('Closed');
          setIsCurrentlyOpen(false);
        }
      }
    };
    fetchHours();
    
    // Check open status every minute
    const interval = setInterval(() => {
      fetchHours();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle scroll for navbar hide/show - use native window scroll on web for reliability
  useEffect(() => {
    if (isWeb) {
      // Use native window scroll event on web
      const handleWindowScroll = () => {
        const value = window.scrollY || document.documentElement.scrollTop;
        
        // Background opacity based on scroll
        if (value > 50 && !isScrolled) {
          setIsScrolled(true);
          Animated.parallel([
            Animated.timing(bgOpacity, { toValue: 1, duration: 150, useNativeDriver: false }),
            Animated.timing(scale, { toValue: 0.98, duration: 150, useNativeDriver: false }),
          ]).start();
        } else if (value <= 50 && isScrolled) {
          setIsScrolled(false);
          Animated.parallel([
            Animated.timing(bgOpacity, { toValue: 0, duration: 150, useNativeDriver: false }),
            Animated.timing(scale, { toValue: 1, duration: 150, useNativeDriver: false }),
          ]).start();
        }

        // Hide/show on scroll direction
        const scrollDelta = value - lastScrollY.current;
        
        if (Math.abs(scrollDelta) > 5) {
          if (scrollDelta > 0 && value > 80) {
            // Scrolling down - hide navbar
            if (!isHidden.current) {
              Animated.timing(translateY, { 
                toValue: -120, 
                duration: 200, 
                useNativeDriver: false 
              }).start();
              isHidden.current = true;
            }
          } else if (scrollDelta < 0) {
            // Scrolling up - show navbar
            if (isHidden.current) {
              Animated.timing(translateY, { 
                toValue: 0, 
                duration: 150, 
                useNativeDriver: false 
              }).start();
              isHidden.current = false;
            }
          }
          lastScrollY.current = value;
        }
      };

      window.addEventListener('scroll', handleWindowScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleWindowScroll);
    } else {
      // Use Animated listener for native
      const listener = scrollY.addListener(({ value }) => {
        if (value > 50 && !isScrolled) {
          setIsScrolled(true);
          Animated.parallel([
            Animated.spring(bgOpacity, { toValue: 1, useNativeDriver: true, tension: 100, friction: 10 }),
            Animated.spring(scale, { toValue: 0.98, useNativeDriver: true, tension: 100, friction: 10 }),
          ]).start();
        } else if (value <= 50 && isScrolled) {
          setIsScrolled(false);
          Animated.parallel([
            Animated.spring(bgOpacity, { toValue: 0, useNativeDriver: true, tension: 100, friction: 10 }),
            Animated.spring(scale, { toValue: 1, useNativeDriver: true, tension: 100, friction: 10 }),
          ]).start();
        }

        const scrollDelta = value - lastScrollY.current;
        if (Math.abs(scrollDelta) > 5) {
          if (scrollDelta > 0 && value > 80 && !isHidden.current) {
            Animated.timing(translateY, { toValue: -120, duration: 200, useNativeDriver: true }).start();
            isHidden.current = true;
          } else if (scrollDelta < 0 && isHidden.current) {
            Animated.timing(translateY, { toValue: 0, duration: 150, useNativeDriver: true }).start();
            isHidden.current = false;
          }
          lastScrollY.current = value;
        }
      });
      return () => scrollY.removeListener(listener);
    }
  }, [isScrolled]);

  // Nav items with their section IDs
  const navItems = [
    { label: 'Home', sectionId: 'hero', isPage: false },
    { label: 'Testimonials', sectionId: 'testimonials', isPage: false },
    { label: 'Services', sectionId: 'services', isPage: false },
    { label: 'Portfolio', sectionId: 'portfolio', isPage: false },
    { label: 'About', sectionId: 'about', isPage: false },
    { label: 'Contact', sectionId: null, isPage: true, path: '/contact' },
  ];

  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    if (item.isPage) {
      navigateTo(item.path);
    } else {
      scrollToSection(item.sectionId);
    }
  };

  return (
    <Animated.View style={[
      styles.navbar,
      { transform: [{ translateY }, { scale }] }
    ]}>
      <Animated.View style={[
        styles.navbarBg,
        { opacity: bgOpacity }
      ]} />
      
      <View style={styles.navbarInner}>
        {/* Logo */}
        <TouchableOpacity style={styles.logoContainer}>
          <Image
            source={require('./assets/logo.png')}
            style={styles.navLogo}
            resizeMode="contain"
          />
          <View style={styles.logoTextWrap}>
            <Text style={styles.logoText}>HANDY</Text>
            <Text style={styles.logoTextAccent}>HAWK</Text>
          </View>
        </TouchableOpacity>

        {/* Desktop Navigation */}
        <View style={styles.navLinks}>
          {navItems.map((item, index) => (
            <TouchableOpacity 
              key={index} 
              style={styles.navLink}
              onPress={() => handleNavClick(item)}
            >
              <Text style={styles.navLinkText}>{item.label}</Text>
              <View style={styles.navLinkGlow} />
            </TouchableOpacity>
          ))}
          {isAdmin && (
            <TouchableOpacity 
              style={[styles.navLink, styles.navLinkAdmin]}
              onPress={() => navigateTo('/admin')}
            >
              <Text style={styles.navLinkTextAdmin}>Admin</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Combined Hours + CTA */}
        <View style={styles.navCTAGroup}>
          {isWeb && width > 768 && (
            <View style={styles.navHoursWrapper}>
              <TouchableOpacity 
                style={styles.navHoursBadge}
                onPress={() => setHoursDropdownOpen(!hoursDropdownOpen)}
                onMouseEnter={() => setHoursDropdownOpen(true)}
                onMouseLeave={() => setHoursDropdownOpen(false)}
              >
                <View style={[
                  styles.navHoursStatusDot,
                  isCurrentlyOpen ? styles.navHoursStatusOpen : styles.navHoursStatusClosed
                ]} />
                <View style={styles.navHoursContent}>
                  <Text style={styles.navHoursLabel}>Today</Text>
                  <Text style={styles.navHoursText}>{todayHours}</Text>
                </View>
                <Text style={styles.navHoursChevron}>▾</Text>
              </TouchableOpacity>
              
              {/* Hours Dropdown */}
              {hoursDropdownOpen && (
                <View 
                  style={styles.hoursDropdown}
                  onMouseEnter={() => setHoursDropdownOpen(true)}
                  onMouseLeave={() => setHoursDropdownOpen(false)}
                >
                  <View style={styles.hoursDropdownHeader}>
                    <View style={[
                      styles.hoursDropdownStatusDot,
                      isCurrentlyOpen ? styles.navHoursStatusOpen : styles.navHoursStatusClosed
                    ]} />
                    <Text style={styles.hoursDropdownTitle}>
                      {isCurrentlyOpen ? 'Open Now' : 'Currently Closed'}
                    </Text>
                  </View>
                  <View style={styles.hoursDropdownDivider} />
                  {weekHours.map((day, index) => (
                    <View 
                      key={index} 
                      style={[
                        styles.hoursDropdownRow,
                        day.isToday && styles.hoursDropdownRowToday
                      ]}
                    >
                      <Text style={[
                        styles.hoursDropdownDay,
                        day.isToday && styles.hoursDropdownDayToday
                      ]}>
                        {day.label}
                      </Text>
                      <Text style={[
                        styles.hoursDropdownTime,
                        !day.isOpen && styles.hoursDropdownTimeClosed
                      ]}>
                        {day.hours}
                      </Text>
                    </View>
                  ))}
                  <View style={styles.hoursDropdownFooter}>
                    <Text style={styles.hoursDropdownTimezone}>Hours in PST</Text>
                  </View>
                </View>
              )}
            </View>
          )}
          <TouchableOpacity 
            style={styles.navCallBtn}
            onPress={() => isWeb ? window.open('tel:+17026602618', '_self') : null}
          >
            <Text style={styles.navCallIcon}>📞</Text>
            <Text style={styles.navCallText}>Call Now</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.navCTA}
            onPress={() => scrollToSection('hero')}
            className={isWeb ? 'shimmer-btn' : undefined}
          >
          <View style={styles.navCTAGlow} />
          <Text style={styles.navCTAText}>Get Estimate</Text>
          <Text style={styles.navCTAIcon}>→</Text>
        </TouchableOpacity>
        </View>

        {/* Mobile Menu Button */}
        <TouchableOpacity 
          style={styles.mobileMenuBtn}
          onPress={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <Animated.View style={[styles.menuLine, mobileMenuOpen && styles.menuLineActive1]} />
          <Animated.View style={[styles.menuLine, mobileMenuOpen && styles.menuLineActive2]} />
          <Animated.View style={[styles.menuLine, mobileMenuOpen && styles.menuLineActive3]} />
        </TouchableOpacity>
      </View>

      {/* Full Screen Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <MobileMenuOverlay 
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          navItems={navItems}
          isAdmin={isAdmin}
          onNavClick={handleNavClick}
          navigateTo={navigateTo}
          scrollToSection={scrollToSection}
          todayHoursText={todayHours}
        />
      )}
    </Animated.View>
  );
};

// Hero Section with AI-Powered Estimate Calculator
const HeroSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', zipcode: '', description: '',
  });
  const [step, setStep] = useState(1); // 1: Info, 2: Description, 3: Results, 4: Schedule
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [businessHours, setBusinessHours] = useState(null);
  const [existingEvents, setExistingEvents] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  const heroScale = useRef(new Animated.Value(0.95)).current;
  const heroOpacity = useRef(new Animated.Value(0)).current;
  const formSlide = useRef(new Animated.Value(100)).current;
  const badgeBounce = useRef(new Animated.Value(0)).current;
  const titleSlide = useRef(new Animated.Value(-30)).current;
  const glowPulse = useRef(new Animated.Value(0.5)).current;
  const formPulse = useRef(new Animated.Value(1)).current;
  const [formHighlight, setFormHighlight] = useState(false);

  useEffect(() => {
    // Staggered entrance animations
    Animated.sequence([
      Animated.parallel([
        Animated.spring(heroScale, { toValue: 1, tension: 50, friction: 8, useNativeDriver: !isWeb }),
        Animated.timing(heroOpacity, { toValue: 1, duration: 800, useNativeDriver: !isWeb }),
      ]),
      Animated.spring(titleSlide, { toValue: 0, tension: 60, friction: 10, useNativeDriver: !isWeb }),
      Animated.spring(badgeBounce, { toValue: 1, tension: 100, friction: 8, useNativeDriver: !isWeb }),
    ]).start();

    // Form slide in
    Animated.spring(formSlide, { toValue: 0, tension: 50, friction: 10, delay: 500, useNativeDriver: !isWeb }).start();

    // Glow pulse animation
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(glowPulse, { toValue: 1, duration: 2000, easing: Easing.inOut(Easing.sin), useNativeDriver: !isWeb }),
        Animated.timing(glowPulse, { toValue: 0.5, duration: 2000, easing: Easing.inOut(Easing.sin), useNativeDriver: !isWeb }),
      ])
    );
    pulseLoop.start();

    return () => pulseLoop.stop();
  }, []);

  // Listen for auto-type event from service cards
  useEffect(() => {
    if (!isWeb) return;
    
    const handleAutoType = (event) => {
      const { text } = event.detail;
      
      // Clear existing description and auto-type the new one
      setFormData(prev => ({ ...prev, description: '' }));
      
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex < text.length) {
          setFormData(prev => ({
            ...prev,
            description: prev.description + text[charIndex]
          }));
          charIndex++;
        } else {
          clearInterval(typeInterval);
        }
      }, 40); // 40ms per character for nice typing effect
    };
    
    window.addEventListener('autoTypeService', handleAutoType);
    return () => window.removeEventListener('autoTypeService', handleAutoType);
  }, []);
  
  // Listen for activate estimate form event (from Get Estimate buttons)
  useEffect(() => {
    if (!isWeb) return;
    
    const handleActivateForm = () => {
      // Reset to step 1 if not already there
      if (step !== 1) {
        setStep(1);
      }
      
      // Trigger highlight effect
      setFormHighlight(true);
      
      // Pulse animation
      Animated.sequence([
        Animated.timing(formPulse, { toValue: 1.02, duration: 150, useNativeDriver: !isWeb }),
        Animated.timing(formPulse, { toValue: 0.98, duration: 150, useNativeDriver: !isWeb }),
        Animated.timing(formPulse, { toValue: 1.01, duration: 100, useNativeDriver: !isWeb }),
        Animated.timing(formPulse, { toValue: 1, duration: 100, useNativeDriver: !isWeb }),
      ]).start();
      
      // Remove highlight after animation
      setTimeout(() => setFormHighlight(false), 1500);
      
      // Focus the first input field
      setTimeout(() => {
        const firstInput = document.querySelector('#estimate-form input');
        if (firstInput) firstInput.focus();
      }, 500);
    };
    
    window.addEventListener('activateEstimateForm', handleActivateForm);
    return () => window.removeEventListener('activateEstimateForm', handleActivateForm);
  }, [step]);

  // Check if zipcode is in Las Vegas area
  const checkZipcode = async (zip) => {
    // Las Vegas area zipcodes (simplified check)
    const vegasZipcodes = ['89', '88'];
    const isVegas = vegasZipcodes.some(prefix => zip.startsWith(prefix));
    
    if (!isVegas) {
      setDistanceInfo({ 
        inServiceArea: false, 
        message: 'This zipcode appears to be outside our service area. Please contact us for a custom quote.' 
      });
      return;
    }

    // Calculate approximate distance from downtown Las Vegas (89101)
    // This is a simplified calculation - the Firebase function does the real work
    try {
      const pricingDoc = await getDoc(doc(db, 'settings', 'pricing'));
      const pricing = pricingDoc.exists() ? pricingDoc.data() : { freeRadius: 15, mileageRate: 2.5 };
      
      // Simplified distance estimation based on zipcode
      // Real calculation happens in the Firebase function
      const estimatedDistance = Math.abs(parseInt(zip.slice(-2)) - 1) * 0.8;
      
      setDistanceInfo({
        inServiceArea: true,
        distance: estimatedDistance,
        freeRadius: pricing.freeRadius || 15,
        mileageRate: pricing.mileageRate || 2.5,
        mileageFee: estimatedDistance > (pricing.freeRadius || 15) 
          ? Math.round((estimatedDistance - (pricing.freeRadius || 15)) * (pricing.mileageRate || 2.5))
          : 0
      });
    } catch (error) {
      console.log('Error checking zipcode:', error);
      setDistanceInfo({ inServiceArea: true, distance: 0, mileageFee: 0 });
    }
  };

  // Generate AI-powered estimate via Firebase Function
  const generateEstimate = async () => {
    if (!formData.description.trim()) {
      alert('Please describe what you need done.');
      return;
    }

    setLoading(true);

    try {
      // Call Firebase Function to generate estimate
      const result = await generateEstimateCallable({
        zipcode: formData.zipcode,
        description: formData.description,
        customerInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          zipcode: formData.zipcode
        }
      });

      const estimateResult = result.data;

      if (!estimateResult.success) {
        throw new Error(estimateResult.error || 'Failed to generate estimate');
      }

      setEstimate(estimateResult);
      setStep(3);

      // Automatically send quote email to customer
      if (formData.email) {
        try {
          await sendEstimateEmail({
            clientEmail: formData.email,
            clientName: formData.name,
            estimateDetails: {
              total: estimateResult.total,
              lowEstimate: estimateResult.lowEstimate,
              highEstimate: estimateResult.highEstimate,
              services: estimateResult.breakdown.map(item => ({
                name: item.name,
                price: item.total
              })),
              description: formData.description
            }
          });
          console.log('Quote email sent to:', formData.email);
        } catch (emailError) {
          console.log('Could not send quote email:', emailError);
          // Don't block the flow if email fails
        }
      }

    } catch (error) {
      console.error('Error generating estimate:', error);
      alert('Failed to generate estimate. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPricing = () => ({
    lineItems: [
      { id: 'plumbing_minor', name: 'Minor Plumbing Repair', price: 85, category: 'plumbing' },
      { id: 'plumbing_major', name: 'Major Plumbing Work', price: 175, category: 'plumbing' },
      { id: 'faucet', name: 'Faucet Replacement', price: 115, category: 'plumbing' },
      { id: 'toilet', name: 'Toilet Repair/Install', price: 135, category: 'plumbing' },
      { id: 'electrical_outlet', name: 'Outlet Installation/Repair', price: 75, category: 'electrical' },
      { id: 'electrical_fixture', name: 'Light Fixture Installation', price: 95, category: 'electrical' },
      { id: 'carpentry_minor', name: 'Minor Carpentry/Trim', price: 75, category: 'carpentry' },
      { id: 'carpentry_door', name: 'Door Installation/Repair', price: 150, category: 'carpentry' },
      { id: 'drywall_patch', name: 'Drywall Patch (Small)', price: 65, category: 'drywall' },
      { id: 'drywall_large', name: 'Drywall Repair (Large)', price: 145, category: 'drywall' },
      { id: 'painting_room', name: 'Room Painting', price: 250, category: 'painting' },
      { id: 'painting_touch', name: 'Touch-up Painting', price: 75, category: 'painting' },
      { id: 'general_repair', name: 'General Home Repair', price: 85, category: 'general' },
      { id: 'assembly', name: 'Furniture Assembly', price: 65, category: 'general' },
      { id: 'mounting', name: 'TV/Shelf Mounting', price: 75, category: 'general' },
    ],
    serviceFee: 50,
    mileageRate: 2.5,
    freeRadius: 15,
    minimumCharge: 99
  });

  const resetForm = () => {
    setStep(1);
    setEstimate(null);
    setDistanceInfo(null);
    setFormData({ name: '', email: '', phone: '', address: '', zipcode: '', description: '' });
  };

  // Render Step 1: Job Description (Start with the problem!)
  const renderStep1 = () => (
    <>
      <View style={styles.estimateHeader}>
        <View style={styles.estimateDot} />
        <Text style={styles.estimateTitle}>Instant Quote</Text>
      </View>
      <Text style={styles.estimateSubtitle}>Tell us what you need - get a quote in seconds</Text>

      <View style={styles.stepIndicator}>
        <View style={[styles.stepDot, styles.stepDotActive]}>
          <Text style={styles.stepDotNum}>1</Text>
        </View>
        <View style={styles.stepLine} />
        <View style={styles.stepDot}>
          <Text style={styles.stepDotNumInactive}>2</Text>
        </View>
        <View style={styles.stepLine} />
        <View style={styles.stepDot}>
          <Text style={styles.stepDotNumInactive}>3</Text>
        </View>
      </View>

      <View style={styles.formField}>
        <Text style={styles.formLabel}>What do you need done?</Text>
        <Text style={styles.formLabelHint}>Be as detailed as possible for the most accurate quote</Text>
        <TextInput
          style={[styles.formInput, styles.formTextareaBig]}
          placeholder="Example: I have a leaky faucet in the kitchen and need two light fixtures installed in the living room. There's also a hole in the drywall near the bedroom door that needs patching..."
          placeholderTextColor={colors.grayDark}
          multiline
          numberOfLines={6}
          value={formData.description}
          onChangeText={(text) => setFormData({ ...formData, description: text })}
        />
      </View>

      <View style={styles.aiHint}>
        <Text style={styles.aiHintIcon}>🤖</Text>
        <Text style={styles.aiHintText}>Our AI analyzes your description to match services and calculate an accurate estimate</Text>
      </View>

      <TouchableOpacity 
        style={styles.formSubmit}
        onPress={() => setStep(2)}
        className={isWeb ? 'shimmer-btn' : undefined}
      >
        <Text style={styles.formSubmitText}>Next: Your Info</Text>
        <Text style={styles.formSubmitArrow}>→</Text>
      </TouchableOpacity>
    </>
  );

  // Render Step 2: Contact Info & Zipcode
  const renderStep2 = () => (
    <>
      <View style={styles.estimateHeader}>
        <TouchableOpacity onPress={() => setStep(1)} style={styles.backBtn}>
          <Text style={styles.backBtnText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.estimateTitle}>Your Information</Text>
      </View>
      <Text style={styles.estimateSubtitle}>We'll send your quote to your email</Text>

      <View style={styles.stepIndicator}>
        <View style={[styles.stepDot, styles.stepDotDone]}>
          <Text style={styles.stepDotCheck}>✓</Text>
        </View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotActive]}>
          <Text style={styles.stepDotNum}>2</Text>
        </View>
        <View style={styles.stepLine} />
        <View style={styles.stepDot}>
          <Text style={styles.stepDotNumInactive}>3</Text>
        </View>
      </View>

      <View style={styles.formField}>
        <Text style={styles.formLabel}>Full Name</Text>
        <TextInput
          style={styles.formInput}
          placeholder="John Smith"
          placeholderTextColor={colors.grayDark}
          value={formData.name}
          onChangeText={(text) => setFormData({ ...formData, name: text })}
        />
      </View>

      <View style={styles.formRow}>
        <View style={[styles.formField, { flex: 1 }]}>
          <Text style={styles.formLabel}>Email</Text>
          <TextInput
            style={styles.formInput}
            placeholder="you@email.com"
            placeholderTextColor={colors.grayDark}
            keyboardType="email-address"
            value={formData.email}
            onChangeText={(text) => setFormData({ ...formData, email: text })}
          />
        </View>
        <View style={[styles.formField, { flex: 1, marginLeft: 12 }]}>
          <Text style={styles.formLabel}>Phone</Text>
          <TextInput
            style={styles.formInput}
            placeholder="(702) 555-0123"
            placeholderTextColor={colors.grayDark}
            keyboardType="phone-pad"
            value={formData.phone}
            onChangeText={(text) => setFormData({ ...formData, phone: text })}
          />
        </View>
      </View>

      <View style={styles.formField}>
        <Text style={styles.formLabel}>Service Zipcode</Text>
        <Text style={styles.formLabelHint}>We'll ask for full address when scheduling</Text>
        <TextInput
          style={styles.formInput}
          placeholder="89101"
          placeholderTextColor={colors.grayDark}
          keyboardType="numeric"
          maxLength={5}
          value={formData.zipcode}
          onChangeText={(text) => {
            setFormData({ ...formData, zipcode: text });
            if (text.length === 5) {
              checkZipcode(text);
            }
          }}
        />
        {distanceInfo && (
          <View style={[
            styles.zipcodeStatus,
            distanceInfo.inServiceArea ? styles.zipcodeStatusGood : styles.zipcodeStatusBad
          ]}>
            <Text style={styles.zipcodeStatusText}>
              {distanceInfo.inServiceArea 
                ? `✓ In service area${distanceInfo.distance > 0 ? ` (${distanceInfo.distance.toFixed(1)} mi)` : ''}`
                : '⚠ Outside service area'}
            </Text>
            {distanceInfo.mileageFee > 0 && (
              <Text style={styles.zipcodeStatusFee}>+${distanceInfo.mileageFee} travel fee</Text>
            )}
          </View>
        )}
      </View>

      <TouchableOpacity 
        style={[styles.formSubmit, loading && styles.formSubmitLoading]}
        onPress={generateEstimate}
        disabled={loading}
        className={isWeb && !loading ? 'shimmer-btn' : undefined}
      >
        {loading ? (
          <>
            <View style={styles.loadingSpinner} />
            <Text style={styles.formSubmitText}>Analyzing...</Text>
          </>
        ) : (
          <>
            <Text style={styles.formSubmitText}>Get My Instant Quote</Text>
            <Text style={styles.formSubmitArrow}>→</Text>
          </>
        )}
      </TouchableOpacity>
    </>
  );

  // Render Step 3: Estimate Results
  const renderStep3 = () => (
    <>
      <View style={styles.estimateHeader}>
        <View style={styles.estimateDot} />
        <Text style={styles.estimateTitle}>Your Estimate</Text>
      </View>

      <View style={styles.stepIndicator}>
        <View style={[styles.stepDot, styles.stepDotDone]}>
          <Text style={styles.stepDotCheck}>✓</Text>
        </View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotDone]}>
          <Text style={styles.stepDotCheck}>✓</Text>
        </View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotActive]}>
          <Text style={styles.stepDotNum}>3</Text>
        </View>
      </View>

      {estimate?.outOfScope ? (
        <View style={styles.outOfScope}>
          <Text style={styles.outOfScopeIcon}>⚠️</Text>
          <Text style={styles.outOfScopeTitle}>This project may require a specialist</Text>
          <Text style={styles.outOfScopeText}>{estimate.outOfScopeReason}</Text>
          <Text style={styles.outOfScopeAction}>Please contact us for a custom assessment.</Text>
        </View>
      ) : (
        <>
          {/* Quote Range */}
          <View style={styles.quoteRange}>
            <Text style={styles.quoteRangeLabel}>Estimated Cost</Text>
            <Text style={styles.quoteRangeValue}>
              ${estimate?.lowEstimate} - ${estimate?.highEstimate}
            </Text>
            <Text style={styles.quoteRangeNote}>Final price may vary based on actual conditions</Text>
          </View>

          {/* Breakdown */}
          <View style={styles.quoteBreakdown}>
            <Text style={styles.quoteBreakdownTitle}>Quote Breakdown</Text>
            
            {estimate?.breakdown?.map((item, index) => (
              <View key={index} style={styles.breakdownItem}>
                <View style={styles.breakdownItemMain}>
                  <Text style={styles.breakdownItemName}>{item.name}</Text>
                  {item.quantity > 1 && (
                    <Text style={styles.breakdownItemQty}>x{item.quantity}</Text>
                  )}
                </View>
                <Text style={styles.breakdownItemPrice}>${item.total}</Text>
              </View>
            ))}

            <View style={styles.breakdownDivider} />

            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownItemName}>Subtotal</Text>
              <Text style={styles.breakdownItemPrice}>${estimate?.subtotal}</Text>
            </View>
            
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownItemName}>Service Fee</Text>
              <Text style={styles.breakdownItemPrice}>${estimate?.serviceFee}</Text>
            </View>

            {estimate?.mileageFee > 0 && (
              <View style={styles.breakdownItem}>
                <Text style={styles.breakdownItemName}>Travel Fee</Text>
                <Text style={styles.breakdownItemPrice}>${estimate?.mileageFee}</Text>
              </View>
            )}

            <View style={styles.breakdownDivider} />

            <View style={styles.breakdownTotal}>
              <Text style={styles.breakdownTotalLabel}>Total Estimate</Text>
              <Text style={styles.breakdownTotalValue}>${estimate?.total}</Text>
            </View>
          </View>

          {/* Complexity & Time */}
          <View style={styles.quoteDetails}>
            <View style={styles.quoteDetailItem}>
              <Text style={styles.quoteDetailIcon}>📊</Text>
              <Text style={styles.quoteDetailLabel}>Complexity</Text>
              <Text style={styles.quoteDetailValue}>{estimate?.complexity}</Text>
            </View>
            <View style={styles.quoteDetailItem}>
              <Text style={styles.quoteDetailIcon}>⏱️</Text>
              <Text style={styles.quoteDetailLabel}>Est. Time</Text>
              <Text style={styles.quoteDetailValue}>{estimate?.estimatedHours}h</Text>
            </View>
          </View>

          {estimate?.additionalNotes && (
            <View style={styles.quoteNotes}>
              <Text style={styles.quoteNotesIcon}>💡</Text>
              <Text style={styles.quoteNotesText}>{estimate.additionalNotes}</Text>
            </View>
          )}
        </>
      )}

      <View style={styles.quoteCTAs}>
        <TouchableOpacity 
          style={[styles.formSubmit, bookingLoading && styles.formSubmitDisabled]} 
          onPress={loadAvailableSlotsAndGoToStep4}
          disabled={bookingLoading}
          className={isWeb && !bookingLoading ? 'shimmer-btn' : undefined}
        >
          <Text style={styles.formSubmitText}>
            {bookingLoading ? 'Loading...' : 'Schedule Service'}
          </Text>
          {!bookingLoading && <Text style={styles.formSubmitArrow}>→</Text>}
        </TouchableOpacity>
        <TouchableOpacity style={styles.quoteSecondaryBtn} onPress={resetForm}>
          <Text style={styles.quoteSecondaryBtnText}>Get New Quote</Text>
        </TouchableOpacity>
      </View>
    </>
  );

  // Load available slots and go to step 4
  const loadAvailableSlotsAndGoToStep4 = async () => {
    setBookingLoading(true);
    try {
      // Load business hours
      const hoursDoc = await getDoc(doc(db, 'settings', 'hours'));
      const hours = hoursDoc.exists() ? hoursDoc.data() : {
        monday: { open: '08:00', close: '17:00', isOpen: true },
        tuesday: { open: '08:00', close: '17:00', isOpen: true },
        wednesday: { open: '08:00', close: '17:00', isOpen: true },
        thursday: { open: '08:00', close: '17:00', isOpen: true },
        friday: { open: '08:00', close: '17:00', isOpen: true },
        saturday: { open: '08:00', close: '17:00', isOpen: true },
        sunday: { open: '08:00', close: '17:00', isOpen: false },
      };
      setBusinessHours(hours);

      // Load existing events from Firestore
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const events = [];
      eventsSnapshot.forEach(doc => {
        events.push({ id: doc.id, ...doc.data() });
      });
      setExistingEvents(events);

      // Generate available slots for next 14 days
      const slots = generateAvailableSlots(hours, events);
      setAvailableSlots(slots);
      
      setStep(4);
    } catch (error) {
      console.error('Error loading slots:', error);
      alert('Failed to load available times. Please try again.');
    }
    setBookingLoading(false);
  };

  // Generate available time slots
  const generateAvailableSlots = (hours, events) => {
    const slots = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = new Date();
    
    for (let d = 1; d <= 14; d++) {
      const date = new Date(today);
      date.setDate(today.getDate() + d);
      
      const dayName = dayNames[date.getDay()];
      const dayHours = hours[dayName];
      
      if (!dayHours || !dayHours.isOpen) continue;
      
      const dateStr = date.toISOString().split('T')[0];
      const openHour = parseInt(dayHours.open.split(':')[0]);
      const closeHour = parseInt(dayHours.close.split(':')[0]);
      
      const dayEvents = events.filter(e => e.date === dateStr);
      const bookedTimes = dayEvents.map(e => e.time);
      
      const daySlots = [];
      for (let hour = openHour; hour < closeHour; hour++) {
        const timeStr = `${hour.toString().padStart(2, '0')}:00`;
        const time12 = hour >= 12 
          ? `${hour === 12 ? 12 : hour - 12}:00 PM` 
          : `${hour}:00 AM`;
        
        const isBooked = bookedTimes.some(t => {
          const bookedHour = parseInt(t.split(':')[0]) || parseInt(t.split(' ')[0]);
          return bookedHour === hour;
        });
        
        if (!isBooked) {
          daySlots.push({
            date: dateStr,
            time: timeStr,
            time12,
            dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
            dateFormatted: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          });
        }
      }
      
      if (daySlots.length > 0) {
        slots.push({
          date: dateStr,
          dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
          dateFormatted: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
          slots: daySlots,
        });
      }
    }
    
    return slots;
  };

  // Book the selected slot
  const bookAppointment = async () => {
    if (!selectedSlot) {
      alert('Please select a time slot');
      return;
    }

    setBookingLoading(true);
    try {
      const serviceName = estimate?.breakdown?.map(b => b.name).join(', ') || formData.description;
      
      // Check if client already exists (by email or phone)
      let clientId = null;
      let isNewClient = true;
      
      if (formData.email || formData.phone) {
        const clientsRef = collection(db, 'clients');
        let existingClientQuery;
        
        if (formData.email) {
          existingClientQuery = query(clientsRef, where('email', '==', formData.email));
        } else {
          existingClientQuery = query(clientsRef, where('phone', '==', formData.phone));
        }
        
        const existingClients = await getDocs(existingClientQuery);
        
        if (!existingClients.empty) {
          // Update existing client (job count will be updated when approved)
          const existingClient = existingClients.docs[0];
          clientId = existingClient.id;
          isNewClient = false;
          
          const existingData = existingClient.data();
          await updateDoc(doc(db, 'clients', clientId), {
            name: formData.name || existingData.name,
            phone: formData.phone || existingData.phone,
            email: formData.email || existingData.email,
            address: formData.address || existingData.address, // Update with full address
            zipcode: formData.zipcode,
            // Don't increment totalJobs until job is approved
            lastDescription: formData.description,
            lastEstimate: estimate,
            type: existingData.type === 'archived' ? 'returning' : existingData.type, // Restore if archived
            updatedAt: serverTimestamp(),
          });
        } else {
          // Create new client with all quote info (as potential until job is approved)
          const clientDoc = await addDoc(collection(db, 'clients'), {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            zipcode: formData.zipcode,
            address: formData.address, // Full address collected at scheduling
            type: 'potential', // Potential until job is approved
            source: 'website_quote',
            totalJobs: 0, // Don't count until job is confirmed
            totalSpent: 0,
            lastJob: null,
            lastDescription: formData.description,
            lastEstimate: estimate,
            notes: `Initial project request: ${formData.description}`,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          clientId = clientDoc.id;
        }
      }
      
      const eventData = {
        clientId: clientId, // Link to client record
        clientName: formData.name,
        clientEmail: formData.email,
        email: formData.email,
        phone: formData.phone,
        address: formData.address, // Full service address
        zipcode: formData.zipcode,
        service: serviceName,
        description: formData.description,
        date: selectedSlot.date,
        time: selectedSlot.time12,
        complexity: estimate?.complexity || 'moderate',
        source: 'website',
        clientType: isNewClient ? 'potential' : 'returning',
        status: 'pending', // Requires admin approval
        estimate: estimate,
        attachedManuals: [], // Field manuals can be attached by admin
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'events'), eventData);
      
      // Send booking confirmation email
      if (formData.email) {
        try {
          await sendBookingConfirmation({
            clientEmail: formData.email,
            clientName: formData.name,
            appointmentDate: new Date(selectedSlot.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }),
            appointmentTime: selectedSlot.time12,
            service: serviceName,
            address: formData.address || `ZIP: ${formData.zipcode}`,
            estimateTotal: estimate ? `${estimate.lowEstimate} - ${estimate.highEstimate}` : null
          });
          console.log('Booking confirmation email sent');
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't fail booking if email fails
        }
      }
      
      setStep(5);
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Failed to book. Please call us directly at (702) 936-9959');
    }
    setBookingLoading(false);
  };

  // Render Step 4: Schedule
  const renderStep4 = () => (
    <>
      <View style={styles.estimateHeader}>
        <TouchableOpacity onPress={() => setStep(3)} style={styles.backBtn}>
          <Text style={styles.backBtnText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.estimateTitle}>Pick a Time</Text>
      </View>
      <Text style={styles.estimateSubtitle}>Select an available time slot</Text>

      <View style={styles.stepIndicator}>
        <View style={[styles.stepDot, styles.stepDotDone]}><Text style={styles.stepDotCheck}>✓</Text></View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotDone]}><Text style={styles.stepDotCheck}>✓</Text></View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotDone]}><Text style={styles.stepDotCheck}>✓</Text></View>
        <View style={[styles.stepLine, styles.stepLineDone]} />
        <View style={[styles.stepDot, styles.stepDotActive]}><Text style={styles.stepDotNum}>4</Text></View>
      </View>

      <View style={styles.bookingSummary}>
        <Text style={styles.bookingSummaryTitle}>📋 {formData.name}</Text>
        <Text style={styles.bookingSummaryTextSmall}>{formData.email}</Text>
        <Text style={styles.bookingSummaryEstimate}>Est. ${estimate?.lowEstimate} - ${estimate?.highEstimate}</Text>
      </View>

      <ScrollView style={styles.slotsContainer} nestedScrollEnabled showsVerticalScrollIndicator={false}>
        {availableSlots.length === 0 ? (
          <View style={styles.noSlotsMessage}>
            <Text style={styles.noSlotsText}>No slots available. Call (702) 936-9959</Text>
          </View>
        ) : (
          availableSlots.slice(0, 5).map((day) => (
            <View key={day.date} style={styles.slotDay}>
              <Text style={styles.slotDayTitle}>{day.dayName}, {day.dateFormatted}</Text>
              <View style={styles.slotTimeGrid}>
                {day.slots.slice(0, 6).map((slot) => (
                  <TouchableOpacity
                    key={`${slot.date}-${slot.time}`}
                    style={[styles.slotTimeBtn, selectedSlot?.date === slot.date && selectedSlot?.time === slot.time && styles.slotTimeBtnSelected]}
                    onPress={() => setSelectedSlot(slot)}
                  >
                    <Text style={[styles.slotTimeText, selectedSlot?.date === slot.date && selectedSlot?.time === slot.time && styles.slotTimeTextSelected]}>
                      {slot.time12}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ))
        )}
      </ScrollView>

      {selectedSlot && (
        <>
          <View style={styles.selectedSlotBanner}>
            <Text style={styles.selectedSlotText}>📅 {selectedSlot.dayName} {selectedSlot.dateFormatted} at {selectedSlot.time12}</Text>
          </View>

          {/* Full Address Collection - After selecting time */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Full Service Address</Text>
            <Text style={styles.formLabelHint}>Where should we come for the appointment?</Text>
            <TextInput
              style={styles.formInput}
              placeholder="123 Main Street, Las Vegas, NV 89101"
              placeholderTextColor={colors.grayDark}
              value={formData.address}
              onChangeText={(text) => setFormData({ ...formData, address: text })}
            />
          </View>
        </>
      )}

      <TouchableOpacity 
        style={[styles.formSubmit, (!selectedSlot || !formData.address.trim() || bookingLoading) && styles.formSubmitDisabled]}
        onPress={bookAppointment}
        disabled={!selectedSlot || !formData.address.trim() || bookingLoading}
        className={isWeb && selectedSlot && formData.address.trim() && !bookingLoading ? 'shimmer-btn' : undefined}
      >
        <Text style={styles.formSubmitText}>{bookingLoading ? 'Booking...' : 'Confirm Booking'}</Text>
        {!bookingLoading && <Text style={styles.formSubmitArrow}>→</Text>}
      </TouchableOpacity>
    </>
  );

  // Render Step 5: Confirmation
  const renderStep5 = () => (
    <View style={styles.confirmationContainer}>
      <View style={styles.confirmationIcon}><Text style={styles.confirmationIconText}>✓</Text></View>
      <Text style={styles.confirmationTitle}>Booking Confirmed!</Text>
      <Text style={styles.confirmationSubtitle}>We'll see you soon</Text>
      <View style={styles.confirmationDetails}>
        <Text style={styles.confirmationValue}>📅 {selectedSlot?.dayName} {selectedSlot?.dateFormatted}</Text>
        <Text style={styles.confirmationValue}>⏰ {selectedSlot?.time12}</Text>
        <Text style={styles.confirmationValue}>💰 ${estimate?.lowEstimate} - ${estimate?.highEstimate}</Text>
      </View>
      <View style={styles.confirmationNote}>
        <Text style={styles.confirmationNoteText}>📧 Confirmation sent to {formData.email}</Text>
      </View>
      <TouchableOpacity style={styles.quoteSecondaryBtn} onPress={resetForm}>
        <Text style={styles.quoteSecondaryBtnText}>Get Another Quote</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.hero} nativeID="hero" {...(isWeb ? { id: 'hero' } : {})}>
      {/* Animated Background */}
      <View style={styles.heroBg}>
        <Animated.View style={[styles.heroBgOrb1, { opacity: glowPulse }]} />
        <Animated.View style={[styles.heroBgOrb2, { opacity: glowPulse }]} />
        <View style={styles.heroBgGrid} />
      </View>

      <Animated.View style={[
        styles.heroContent,
        { opacity: heroOpacity, transform: [{ scale: heroScale }] }
      ]}>
        {/* Left Side - Text */}
        <View style={styles.heroLeft}>
          <Animated.View style={[
            styles.heroBadge,
            { transform: [{ scale: badgeBounce }] }
          ]}>
            <View style={styles.heroBadgePulse} />
            <View style={styles.heroBadgeDot} />
            <Text style={styles.heroBadgeText}>Now Serving Las Vegas & Surrounding Areas</Text>
          </Animated.View>

          <Animated.Text style={[
            styles.heroTitle,
            { transform: [{ translateX: titleSlide }] }
          ]}>
            Your Home.{'\n'}
            <Text style={styles.heroTitleGradient}>Our Craft.</Text>
          </Animated.Text>

          <Text style={styles.heroSubtitle}>
            Professional handyman services that arrive fast, work clean, 
            and get it done right. <Text style={styles.heroSubtitleAccent}>The first time.</Text>
          </Text>

          {/* Trust Indicators */}
          <View style={styles.trustRow}>
            <View style={styles.trustItem}>
              <Text style={styles.trustIcon}>✓</Text>
              <Text style={styles.trustText}>Licensed & Insured</Text>
            </View>
            <View style={styles.trustItem}>
              <Text style={styles.trustIcon}>✓</Text>
              <Text style={styles.trustText}>Same-Day Available</Text>
            </View>
            <View style={styles.trustItem}>
              <Text style={styles.trustIcon}>✓</Text>
              <Text style={styles.trustText}>Satisfaction Guaranteed</Text>
            </View>
          </View>

          {/* Stats */}
          <View style={styles.heroStats}>
            {[
              { number: '20+', label: 'Years' },
              { number: '2K+', label: 'Projects' },
              { number: '5.0', label: '★ Google' },
            ].map((stat, index) => (
              <View key={index} style={styles.heroStatItem}>
                <Text style={styles.heroStatNumber}>{stat.number}</Text>
                <Text style={styles.heroStatLabel}>{stat.label}</Text>
              </View>
            ))}
          </View>

          {/* CTAs */}
          <View style={styles.heroCTAs}>
            <TouchableOpacity style={styles.heroPrimaryBtn} onPress={() => {
              // Directly activate the form since we're already on the hero section
              if (isWeb && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('activateEstimateForm'));
              }
            }}>
              <View style={styles.heroPrimaryBtnGlow} />
              <Text style={styles.heroPrimaryBtnText}>Get Instant Quote</Text>
              <Text style={styles.heroPrimaryBtnArrow}>→</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.heroSecondaryBtn} onPress={() => scrollToSection('portfolio')}>
              <View style={styles.playIconWrap}>
                <Text style={styles.playIcon}>▶</Text>
              </View>
              <Text style={styles.heroSecondaryBtnText}>See Our Work</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Right Side - Enhanced Estimate Calculator */}
        <Animated.View style={[
          styles.heroRight,
          { transform: [{ translateX: formSlide }] }
        ]}>
          <Animated.View 
            nativeID="estimate-form"
            style={[
              styles.estimateCard,
              { transform: [{ scale: formPulse }] },
              formHighlight && styles.estimateCardHighlight
            ]}
          >
            <View style={styles.estimateCardGlow} />
            
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}

            {step < 3 && (
            <View style={styles.formNote}>
                <Text style={styles.formNoteIcon}>🔒</Text>
                <Text style={styles.formNoteText}>Your info is safe & secure</Text>
            </View>
            )}
          </Animated.View>
          
          {/* Call Us Now Button - Below the form */}
          <TouchableOpacity 
            style={styles.heroCallBtn}
            onPress={() => isWeb ? window.open('tel:+17029369959', '_self') : null}
          >
            <Text style={styles.heroCallIcon}>📞</Text>
            <View style={styles.heroCallContent}>
              <Text style={styles.heroCallLabel}>Prefer to talk?</Text>
              <Text style={styles.heroCallNumber}>(702) 936-9959</Text>
            </View>
            <Text style={styles.heroCallArrow}>→</Text>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>

      {/* Scroll Indicator */}
      <View style={styles.scrollIndicator}>
        <Text style={styles.scrollText}>Scroll to explore</Text>
        <View style={styles.scrollLine}>
          <View style={styles.scrollDot} />
        </View>
      </View>
    </View>
  );
};

// Animated Marquee - Now uses dynamic services
const ServicesMarquee = ({ services }) => {
  const scrollAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animate = () => {
      scrollAnim.setValue(0);
      Animated.timing(scrollAnim, {
        toValue: -1500,
        duration: 25000,
        easing: Easing.linear,
        useNativeDriver: !isWeb,
      }).start(() => animate());
    };
    animate();
  }, []);

  // Get service titles for marquee, filter to active only
  const activeServices = services.filter(s => s.active !== false);
  const items = activeServices.length > 0 
    ? activeServices.map(s => s.title)
    : ['Plumbing', 'Electrical', 'Carpentry', 'Painting', 'Renovations', 'Roofing'];
  const tripled = [...items, ...items, ...items];

  return (
    <View style={styles.marquee}>
      <Animated.View style={[styles.marqueeTrack, { transform: [{ translateX: scrollAnim }] }]}>
        {tripled.map((item, index) => (
          <View key={index} style={styles.marqueeItem}>
            <View style={styles.marqueeDot} />
            <Text style={styles.marqueeText}>{item}</Text>
          </View>
        ))}
      </Animated.View>
    </View>
  );
};

// Default auto-type text (fallback if not set on service)
const defaultAutoTypeText = 'I need help with a home improvement project. ';

// Flip Service Card Component
const FlipServiceCard = ({ service, index, onGetEstimate }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const flipAnim = useRef(new Animated.Value(0)).current;
  const isMobile = width < 768;
  
  const emoji = service.icon || iconToEmoji[service.iconName] || '🔧';
  
  const handleFlip = () => {
    const toValue = isFlipped ? 0 : 1;
    Animated.timing(flipAnim, {
      toValue,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: !isWeb,
    }).start();
    setIsFlipped(!isFlipped);
  };
  
  const handleMouseEnter = () => {
    if (!isMobile && !isFlipped) {
      Animated.timing(flipAnim, {
        toValue: 1,
        duration: 250,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: !isWeb,
      }).start();
      setIsFlipped(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (!isMobile && isFlipped) {
      Animated.timing(flipAnim, {
        toValue: 0,
        duration: 250,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: !isWeb,
      }).start();
      setIsFlipped(false);
    }
  };
  
  const frontRotate = flipAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });
  
  const backRotate = flipAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['-180deg', '0deg'],
  });
  
  // Get detailed info for back of card
  const detailedInfo = service.detailedDesc || `Our ${service.title.toLowerCase()} services include professional assessment, quality materials, and expert craftsmanship. We handle everything from minor repairs to major installations.`;
  
  return (
    <View 
      style={styles.flipCardContainer}
      {...(isWeb ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      } : {})}
    >
      {/* Front of Card */}
      <Animated.View
        style={[
          styles.serviceCard,
          styles.flipCardFace,
          isWeb ? {
            transform: [{ rotateY: frontRotate }],
          } : {
            opacity: isFlipped ? 0 : 1,
            zIndex: isFlipped ? 0 : 1,
          }
        ]}
      >
        <TouchableOpacity
          style={styles.flipCardTouchable}
          onPress={isMobile ? handleFlip : undefined}
          activeOpacity={0.9}
        >
          <View style={[styles.serviceIconBg, { backgroundColor: service.color + '15' }]}>
            <Text style={styles.serviceIcon}>{emoji}</Text>
          </View>
          <Text style={styles.serviceTitle}>{service.title}</Text>
          <Text style={styles.serviceDesc}>{service.desc}</Text>
          <View style={styles.serviceArrow}>
            <Text style={[styles.serviceArrowText, { color: service.color }]}>
              {isMobile ? '↻' : '→'}
            </Text>
          </View>
          <View style={[styles.serviceAccent, { backgroundColor: service.color }]} />
        </TouchableOpacity>
      </Animated.View>
      
      {/* Back of Card */}
      <Animated.View
        style={[
          styles.serviceCard,
          styles.serviceCardBack,
          styles.flipCardFace,
          { backgroundColor: service.color + '15' },
          isWeb ? {
            transform: [{ rotateY: backRotate }],
          } : {
            opacity: isFlipped ? 1 : 0,
            zIndex: isFlipped ? 1 : 0,
          }
        ]}
      >
        <TouchableOpacity
          style={styles.flipCardTouchable}
          onPress={isMobile ? handleFlip : undefined}
          activeOpacity={0.9}
        >
          <View style={[styles.serviceBackHeader, { borderBottomColor: service.color + '40' }]}>
            <Text style={styles.serviceIcon}>{emoji}</Text>
            <Text style={[styles.serviceBackTitle, { color: service.color }]}>{service.title}</Text>
          </View>
          <Text style={styles.serviceBackDesc}>{detailedInfo}</Text>
          <TouchableOpacity 
            style={[styles.serviceGetEstimateBtn, { backgroundColor: service.color }]}
            onPress={(e) => {
              e.stopPropagation();
              onGetEstimate(service);
            }}
          >
            <Text style={styles.serviceGetEstimateBtnText}>Get Estimate →</Text>
          </TouchableOpacity>
          {isMobile && (
            <Text style={styles.tapToFlipBack}>Tap to flip back</Text>
          )}
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
};

// Services Section - Now loads from Firestore
const ServicesSection = ({ services, onServiceSelect }) => {
  // Filter to only active services, limit to 8 for display (4 on mobile)
  const isMobileSvc = width < 600;
  const displayServices = services
    .filter(s => s.active !== false)
    .slice(0, isMobileSvc ? 4 : 8);

  const handleGetEstimate = (service) => {
    // Scroll to hero section
    scrollToSection('hero');
    
    // Use service's autoTypeText or fallback to default
    const starter = service.autoTypeText || defaultAutoTypeText;
    
    // Dispatch custom event that HeroSection will listen to
    if (isWeb) {
      window.dispatchEvent(new CustomEvent('autoTypeService', { 
        detail: { text: starter, serviceTitle: service.title } 
      }));
    }
  };

  return (
    <AnimatedSection style={styles.servicesSection} delay={100} nativeID="services" id="services">
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTag}>WHAT WE DO</Text>
        <Text style={styles.sectionTitle}>
          Services Built Around{'\n'}
          <Text style={styles.sectionTitleAccent}>Your Needs</Text>
        </Text>
        <Text style={styles.sectionDesc}>
          From quick fixes to complete renovations, we've got the skills and tools 
          to handle any job around your home.
        </Text>
      </View>

      <View style={styles.servicesGrid}>
        {displayServices.map((service, index) => (
          <FlipServiceCard 
            key={service.id || index}
            service={service}
            index={index}
            onGetEstimate={handleGetEstimate}
          />
        ))}
      </View>

      <TouchableOpacity style={styles.servicesCTA} onPress={() => scrollToSection('hero')}>
        <Text style={styles.servicesCTAText}>🦅 Get Your Free Estimate Today</Text>
        <Text style={styles.servicesCTAArrow}>→</Text>
      </TouchableOpacity>
    </AnimatedSection>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [items, setItems] = useState(portfolioItems); // Default fallback
  const [selectedItem, setSelectedItem] = useState(null);
  const filters = ['All', 'Renovation', 'Carpentry', 'Plumbing', 'Painting'];

  // Load portfolio items from Firestore and merge with defaults
  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const portfolioRef = collection(db, 'portfolio');
        const snapshot = await getDocs(portfolioRef);
        
        // Create a map of Firestore items by title (lowercase for matching)
        const firestoreItems = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.active !== false) {
            const key = data.title?.toLowerCase();
            if (key) {
              firestoreItems[key] = { id: doc.id, ...data };
            }
          }
        });
        
        // Merge: use Firestore data where available, keep placeholders otherwise
        const mergedItems = portfolioItems.map((defaultItem, idx) => {
          const key = defaultItem.title.toLowerCase();
          const firestoreItem = firestoreItems[key];
          
          if (firestoreItem) {
            // Firestore has data for this item - use it
            return {
              ...defaultItem,
              ...firestoreItem,
              order: firestoreItem.order ?? idx,
            };
          }
          // No Firestore data - keep placeholder
          return { ...defaultItem, order: idx };
        });
        
        // Also add any Firestore items that don't match defaults (custom items)
        Object.values(firestoreItems).forEach(item => {
          const exists = mergedItems.some(m => m.title?.toLowerCase() === item.title?.toLowerCase());
          if (!exists) {
            mergedItems.push(item);
          }
        });
        
        // Sort by order
        mergedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
        setItems(mergedItems);
        
      } catch (error) {
        console.error('Error loading portfolio:', error);
        // Keep using default portfolio items on error
      }
    };
    loadPortfolio();
  }, []);

  // Filter items
  const filteredItems = activeFilter === 'All' 
    ? items 
    : items.filter(item => item.category === activeFilter);

  return (
    <AnimatedSection style={styles.portfolioSection} delay={200} nativeID="portfolio" id="portfolio">
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTag}>OUR WORK</Text>
        <Text style={styles.sectionTitle}>
          Projects That{'\n'}
          <Text style={styles.sectionTitleAccent}>Speak For Themselves</Text>
        </Text>
      </View>

      <View style={styles.filterBar}>
        {filters.map((filter, index) => (
          <TouchableOpacity
            key={index}
            style={[styles.filterBtn, activeFilter === filter && styles.filterBtnActive]}
            onPress={() => setActiveFilter(filter)}
          >
            <Text style={[styles.filterBtnText, activeFilter === filter && styles.filterBtnTextActive]}>
              {filter}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.portfolioGrid}>
        {filteredItems.map((item, index) => (
          <TouchableOpacity 
            key={item.id} 
            style={[
            styles.portfolioCard,
            index === 0 && styles.portfolioCardLarge
            ]}
            onPress={() => item.url && setSelectedItem(item)}
          >
            {item.imageUrl || item.thumbnailUrl ? (
              <Image 
                source={{ uri: item.thumbnailUrl || item.imageUrl }} 
                style={styles.portfolioCardImage}
                resizeMode="cover"
              />
            ) : (
            <View style={[styles.portfolioCardBg, { backgroundColor: item.color + '25' }]}>
              <Text style={styles.portfolioCardIcon}>
                {item.type === 'video' ? '▶' : '📷'}
              </Text>
            </View>
            )}
            <View style={styles.portfolioOverlay}>
              <Text style={[styles.portfolioCategory, { color: item.color || colors.teal }]}>{item.category}</Text>
              <Text style={styles.portfolioTitle}>{item.title}</Text>
            </View>
            {item.type === 'video' && (
              <View style={[styles.playBadge, { backgroundColor: item.color || colors.teal }]}>
                <Text style={styles.playBadgeIcon}>▶</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>

      {/* Lightbox Modal for viewing media */}
      {selectedItem && (
        <Modal visible={true} transparent animationType="fade">
          <View style={styles.portfolioLightbox}>
            <TouchableOpacity style={styles.portfolioLightboxClose} onPress={() => setSelectedItem(null)}>
              <Text style={styles.portfolioLightboxCloseText}>✕</Text>
            </TouchableOpacity>
            <View style={styles.portfolioLightboxContent}>
              {selectedItem.type === 'video' ? (
                isWeb ? (
                  <iframe 
                    src={selectedItem.url}
                    style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }}
                    allowFullScreen
                  />
                ) : (
                  <Text style={styles.portfolioLightboxText}>Video: {selectedItem.title}</Text>
                )
              ) : (
                <Image 
                  source={{ uri: selectedItem.url || selectedItem.imageUrl }} 
                  style={styles.portfolioLightboxImage}
                  resizeMode="contain"
                />
              )}
            </View>
            <Text style={styles.portfolioLightboxTitle}>{selectedItem.title}</Text>
            {selectedItem.description && (
              <Text style={styles.portfolioLightboxDesc}>{selectedItem.description}</Text>
            )}
          </View>
        </Modal>
      )}
    </AnimatedSection>
  );
};

// About Section
const AboutSection = () => (
  <AnimatedSection style={styles.aboutSection} delay={200} nativeID="about" id="about">
    <View style={styles.aboutContent}>
      <View style={styles.aboutLeft}>
        <View style={styles.aboutImageWrap}>
          <Image
            source={require('./assets/logo.png')}
            style={styles.aboutImage}
            resizeMode="contain"
          />
          <View style={styles.aboutBadge}>
            <Text style={styles.aboutBadgeNumber}>20+</Text>
            <Text style={styles.aboutBadgeText}>Years</Text>
          </View>
        </View>
      </View>

      <View style={styles.aboutRight}>
        <Text style={styles.sectionTag}>ABOUT US</Text>
        <Text style={styles.aboutTitle}>
          Built on Trust.{'\n'}
          <Text style={styles.aboutTitleAccent}>Driven by Excellence.</Text>
        </Text>
        <Text style={styles.aboutDesc}>
          HandyHawk started with a simple belief: every homeowner deserves access to 
          reliable, skilled professionals who treat their home with respect.
        </Text>

        <View style={styles.aboutFeatures}>
          {['Licensed & Insured', 'Same-Day Service', 'Transparent Pricing', 'Satisfaction Guaranteed'].map((f, i) => (
            <View key={i} style={styles.aboutFeature}>
              <View style={styles.aboutFeatureCheck}>
                <Text style={styles.aboutFeatureCheckIcon}>✓</Text>
              </View>
              <Text style={styles.aboutFeatureText}>{f}</Text>
            </View>
          ))}
        </View>

        <TouchableOpacity style={styles.aboutBtn}>
          <Text style={styles.aboutBtnText}>Learn More About Us</Text>
          <Text style={styles.aboutBtnArrow}>→</Text>
        </TouchableOpacity>
      </View>
    </View>
  </AnimatedSection>
);

// Testimonials Carousel
const TestimonialsSection = () => {
  const scrollViewRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(width);
  const autoScrollRef = useRef(null);
  
  // Responsive: 3 cards on desktop/tablet, 1 on mobile
  const isMobile = containerWidth < 768;
  const cardsPerPage = isMobile ? 1 : 3;
  const totalCards = testimonials.length;
  const maxIndex = isMobile ? totalCards - 1 : Math.max(0, totalCards - cardsPerPage);
  
  // Card width calculation - fixed width cards for bleeding effect
  const gap = 24;
  const cardWidth = isMobile 
    ? Math.min(containerWidth - 80, 340) // Mobile: single card with peek
    : 360; // Desktop: fixed width cards that bleed off edges

  // Start/restart auto-scroll
  const startAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = prev + 1;
        return next > maxIndex ? 0 : next;
      });
    }, 5000);
  };

  // Auto-scroll every 5 seconds
  useEffect(() => {
    startAutoScroll();
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, [maxIndex]);

  // Scroll to current index
  useEffect(() => {
    if (scrollViewRef.current) {
      const scrollTo = currentIndex * (cardWidth + gap);
      scrollViewRef.current.scrollTo({ x: scrollTo, animated: true });
    }
  }, [currentIndex, cardWidth]);

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const next = prev + 1;
      return next > maxIndex ? 0 : next;
    });
    startAutoScroll(); // Reset auto-scroll timer
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => {
      const next = prev - 1;
      return next < 0 ? maxIndex : next;
    });
    startAutoScroll(); // Reset auto-scroll timer
  };

  const handleScrollEnd = (event) => {
    const scrollX = event.nativeEvent.contentOffset.x;
    const newIndex = Math.round(scrollX / (cardWidth + gap));
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex <= maxIndex) {
      setCurrentIndex(newIndex);
      startAutoScroll();
    }
  };

  const onLayout = (event) => {
    const { width: layoutWidth } = event.nativeEvent.layout;
    setContainerWidth(layoutWidth); // Use full width for bleeding effect
  };

  // Pagination dots
  const totalDots = isMobile ? totalCards : Math.ceil(totalCards / cardsPerPage);

  return (
    <AnimatedSection style={styles.testimonialsSection} delay={300}>
      <View style={[styles.sectionHeader, { paddingHorizontal: 24 }]}>
        <Text style={[styles.sectionTag, { color: colors.teal }]}>TESTIMONIALS</Text>
        <Text style={[styles.sectionTitle, { color: colors.white }]}>
          Real Stories From{'\n'}
          <Text style={styles.sectionTitleAccent}>Real Customers</Text>
        </Text>
        <Text style={styles.testimonialCount}>
          {testimonials.length} verified reviews from Google
        </Text>
      </View>

      <View style={styles.carouselContainer} onLayout={onLayout}>
        {/* Left Arrow */}
          <TouchableOpacity
          style={[styles.carouselArrow, styles.carouselArrowLeft]}
          onPress={goToPrev}
          activeOpacity={0.7}
        >
          <Text style={styles.carouselArrowText}>‹</Text>
        </TouchableOpacity>

        {/* Carousel Track */}
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          decelerationRate="fast"
          snapToInterval={cardWidth + gap}
          snapToAlignment="start"
          contentContainerStyle={[
            styles.carouselTrack,
            { 
              paddingHorizontal: isMobile ? 40 : 80, // Offset to show partial cards peeking
              paddingLeft: isMobile ? 40 : Math.max(80, (containerWidth - (cardWidth * 3 + gap * 2)) / 2 - 60),
            }
          ]}
          onMomentumScrollEnd={handleScrollEnd}
          scrollEventThrottle={16}
        >
          {testimonials.map((t, index) => (
            <View
            key={index}
            style={[
              styles.testimonialCard,
                { 
                  width: cardWidth,
                  marginRight: gap 
                }
            ]}
          >
            <Text style={styles.testimonialQuote}>"</Text>
            <View style={styles.testimonialStars}>
              {[...Array(t.rating)].map((_, i) => (
                <Text key={i} style={styles.star}>★</Text>
              ))}
            </View>
              <Text style={styles.testimonialText} numberOfLines={5}>
                {t.text}
              </Text>
            <View style={styles.testimonialAuthor}>
              <View style={styles.authorAvatar}>
                <Text style={styles.authorInitial}>{t.name[0]}</Text>
              </View>
                <View style={styles.authorInfo}>
                <Text style={styles.authorName}>{t.name}</Text>
                <Text style={styles.authorLocation}>{t.location}</Text>
              </View>
            </View>
            </View>
          ))}
        </ScrollView>

        {/* Right Arrow */}
        <TouchableOpacity 
          style={[styles.carouselArrow, styles.carouselArrowRight]}
          onPress={goToNext}
          activeOpacity={0.7}
        >
          <Text style={styles.carouselArrowText}>›</Text>
        </TouchableOpacity>
      </View>

      {/* Pagination Dots */}
      <View style={styles.carouselPagination}>
        {[...Array(totalDots)].map((_, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.carouselDot,
              (isMobile ? index === currentIndex : Math.floor(currentIndex / cardsPerPage) === index) && styles.carouselDotActive
            ]}
            onPress={() => {
              setCurrentIndex(isMobile ? index : index * cardsPerPage);
              startAutoScroll();
            }}
          />
        ))}
      </View>

      {/* Swipe hint on mobile */}
      {isMobile && (
        <Text style={styles.swipeHint}>← Swipe to see more →</Text>
      )}
    </AnimatedSection>
  );
};

// CTA Section
const CTASection = () => {
  const handleCallNow = () => {
    if (isWeb && typeof window !== 'undefined') {
      window.location.href = 'tel:+17029369959';
    }
  };
  
  const handleScheduleOnline = () => {
    scrollToSection('hero');
  };
  
  return (
  <AnimatedSection style={styles.ctaSection} delay={400}>
    <View style={styles.ctaGlow} />
    <View style={styles.ctaContent}>
      <Text style={styles.ctaTitle}>Ready to Get Started?</Text>
      <Text style={styles.ctaSubtitle}>
        Get a free estimate today. No obligations, no pressure.
      </Text>
      <View style={styles.ctaButtons}>
          <TouchableOpacity style={styles.ctaPrimaryBtn} onPress={handleCallNow}>
          <Text style={styles.ctaPrimaryBtnText}>📞 Call Now</Text>
        </TouchableOpacity>
          <TouchableOpacity style={styles.ctaSecondaryBtn} onPress={handleScheduleOnline}>
          <Text style={styles.ctaSecondaryBtnText}>Schedule Online →</Text>
        </TouchableOpacity>
      </View>
    </View>
  </AnimatedSection>
);
};

// Footer
const Footer = () => (
  <View style={styles.footer}>
    <View style={styles.footerTop}>
      <View style={styles.footerBrand}>
        <View style={styles.footerLogoWrap}>
          <Image source={require('./assets/logo.png')} style={styles.footerLogo} resizeMode="contain" />
          <Text style={styles.footerLogoText}>HANDY<Text style={styles.footerLogoAccent}>HAWK</Text></Text>
        </View>
        <Text style={styles.footerDesc}>
          Professional handyman services for Las Vegas and surrounding areas.
        </Text>
        <View style={styles.socialLinks}>
          {['f', 'in', '𝕏', 'IG'].map((icon, i) => (
            <TouchableOpacity key={i} style={styles.socialBtn}>
              <Text style={styles.socialIcon}>{icon}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.footerLinks}>
        {[
          { title: 'Services', links: ['General Repairs', 'Carpentry', 'Plumbing', 'Electrical'] },
          { title: 'Company', links: ['About', 'Portfolio', 'Careers', 'Contact'] },
        ].map((col, i) => (
          <View key={i} style={styles.footerCol}>
            <Text style={styles.footerColTitle}>{col.title}</Text>
            {col.links.map((link, j) => (
              <TouchableOpacity key={j}><Text style={styles.footerLink}>{link}</Text></TouchableOpacity>
            ))}
          </View>
        ))}
        <View style={styles.footerCol}>
          <Text style={styles.footerColTitle}>Contact</Text>
          <Text style={styles.footerContact}>📍 Las Vegas, Nevada</Text>
          <Text style={styles.footerContact}>📞 (702) 936-9959</Text>
          <Text style={styles.footerContact}>✉️ info@handyhawk.net</Text>
        </View>
      </View>
    </View>

    <View style={styles.footerBottom}>
      <Text style={styles.copyright}>© 2024 HandyHawk. All rights reserved.</Text>
      <View style={styles.footerLegal}>
        <Text style={styles.legalLink}>Privacy</Text>
        <Text style={styles.legalDivider}>•</Text>
        <Text style={styles.legalLink}>Terms</Text>
      </View>
    </View>
  </View>
);

// Home Page Component
function HomePage() {
  const scrollY = useRef(new Animated.Value(0)).current;
  const [services, setServices] = useState(defaultServices);
  const [servicesLoaded, setServicesLoaded] = useState(false);

  // Handle scroll with manual callback for reliable tracking on web
  const handleScroll = (event) => {
    const y = event.nativeEvent.contentOffset.y;
    scrollY.setValue(y);
  };

  // Load services from Firestore on mount
  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesDoc = await getDoc(doc(db, 'settings', 'services'));
        if (servicesDoc.exists() && servicesDoc.data().items) {
          setServices(servicesDoc.data().items);
        }
      } catch (error) {
        console.log('Using default services');
      }
      setServicesLoaded(true);
    };
    loadServices();
  }, []);

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <Navbar scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        onScroll={isWeb ? handleScroll : Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
        scrollEventThrottle={16}
      >
        <HeroSection />
        <ServicesMarquee services={services} />
        <View nativeID="testimonials">
          <TestimonialsSection />
        </View>
        <View nativeID="services">
          <ServicesSection services={services} />
        </View>
        <View nativeID="portfolio">
        <PortfolioSection />
        </View>
        <View nativeID="about">
        <AboutSection />
        </View>
        <CTASection />
        <Footer />
      </Animated.ScrollView>
    </View>
  );
}

// Main App with Router
export default function App() {
  const { path } = useRouter();

  // Route to admin page
  if (path === '/admin' || path === '/admin/') {
    return <AdminScreen />;
  }

  // Route to checkout page (e.g., /checkout/clientId or /checkout/clientId/jobId)
  if (path.startsWith('/checkout')) {
    const parts = path.split('/').filter(Boolean);
    const clientId = parts[1] || null;
    const jobId = parts[2] || null;
    
    // Lazy load CheckoutScreen
    const CheckoutScreen = require('./screens/CheckoutScreen').default;
    return <CheckoutScreen clientId={clientId} jobId={jobId} />;
  }

  // Route to invoice payment page (e.g., /pay/invoiceId)
  if (path.startsWith('/pay/')) {
    const parts = path.split('/').filter(Boolean);
    const invoiceId = parts[1] || null;
    
    return <InvoicePayScreen invoiceId={invoiceId} />;
  }

  // Route to contact page
  if (path === '/contact' || path === '/contact/') {
    return (
      <AuthProvider>
        <ContactScreen />
      </AuthProvider>
    );
  }

  // Default to home page
  return (
    <AuthProvider>
      <HomePage />
    </AuthProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.darkNavy,
  },
  scrollView: {
    flex: 1,
  },

  // ========== NAVBAR ==========
  navbar: {
    position: isWeb ? 'fixed' : 'absolute',
    top: 16,
    left: 16,
    right: 16,
    zIndex: 1000,
    borderRadius: 16,
    overflow: 'visible', // Changed to visible so dropdown can appear outside
  },
  navbarBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.glass,
    backdropFilter: 'blur(20px)',
    borderWidth: 1,
    borderColor: colors.glassBorder,
    borderRadius: 16,
    overflow: 'hidden', // Keep the glass effect contained
  },
  navbarInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  navLogo: {
    width: 40,
    height: 40,
    borderRadius: 10,
  },
  logoTextWrap: {
    flexDirection: 'row',
    marginLeft: 10,
  },
  logoText: {
    fontSize: 17,
    fontWeight: '800',
    color: colors.white,
    letterSpacing: 0.5,
  },
  logoTextAccent: {
    fontSize: 17,
    fontWeight: '800',
    color: colors.teal,
    letterSpacing: 0.5,
  },
  navLinks: {
    flexDirection: 'row',
    gap: 8,
    display: isWeb && width > 900 ? 'flex' : 'none',
  },
  navLink: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    position: 'relative',
  },
  navLinkText: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '500',
  },
  navLinkGlow: {
    position: 'absolute',
    bottom: 8,
    left: '50%',
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.teal,
    opacity: 0,
  },
  navLinkAdmin: {
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
  },
  navLinkTextAdmin: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  navCTAGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    display: isWeb && width > 500 ? 'flex' : 'none',
  },
  navHoursWrapper: {
    position: 'relative',
  },
  navHoursBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 8,
    paddingHorizontal: 14,
    height: 48,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
    gap: 10,
    borderWidth: 1,
    borderRightWidth: 0,
    borderColor: colors.glassBorder,
    cursor: 'pointer',
  },
  navHoursStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  navHoursStatusOpen: {
    backgroundColor: '#58d68d', // Teal/green color
    ...(isWeb ? { boxShadow: '0 0 8px rgba(88, 214, 141, 0.8)' } : {}),
  },
  navHoursStatusClosed: {
    backgroundColor: colors.red || '#f85149',
  },
  navHoursContent: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  navHoursLabel: {
    color: colors.gray,
    fontSize: 9,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  navHoursText: {
    color: colors.grayLight,
    fontSize: 12,
    fontWeight: '600',
  },
  navHoursChevron: {
    color: colors.gray,
    fontSize: 10,
    marginLeft: 2,
  },
  // Hours Dropdown
  hoursDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: colors.navy,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    minWidth: 220,
    ...(isWeb ? { boxShadow: '0 10px 40px rgba(0,0,0,0.5)' } : {}),
    zIndex: 9999,
    overflow: 'hidden',
  },
  hoursDropdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 10,
    backgroundColor: colors.navyLight,
  },
  hoursDropdownStatusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  hoursDropdownTitle: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  hoursDropdownDivider: {
    height: 1,
    backgroundColor: colors.glassBorder,
  },
  hoursDropdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  hoursDropdownRowToday: {
    backgroundColor: colors.teal + '10',
  },
  hoursDropdownDay: {
    color: colors.gray,
    fontSize: 13,
    fontWeight: '500',
  },
  hoursDropdownDayToday: {
    color: colors.teal,
    fontWeight: '600',
  },
  hoursDropdownTime: {
    color: colors.grayLight,
    fontSize: 13,
    fontWeight: '500',
  },
  hoursDropdownTimeClosed: {
    color: colors.gray,
  },
  hoursDropdownFooter: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  hoursDropdownTimezone: {
    color: colors.grayDark,
    fontSize: 10,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  navCallBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.navyLight,
    paddingHorizontal: 16,
    height: 48,
    gap: 8,
    display: isWeb && width > 768 ? 'flex' : 'none',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  navCallIcon: {
    fontSize: 14,
    color: colors.teal,
  },
  navCallText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  navCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    paddingHorizontal: 20,
    height: 48,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    gap: 8,
    position: 'relative',
    overflow: 'hidden',
  },
  navCTAGlow: {
    position: 'absolute',
    top: -20,
    right: -20,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  navCTAText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  navCTAIcon: {
    color: colors.darkNavy,
    fontSize: 16,
  },
  mobileMenuBtn: {
    width: 32,
    height: 24,
    justifyContent: 'space-between',
    display: isWeb && width > 600 ? 'none' : 'flex',
    zIndex: 1001,
  },
  menuLine: {
    width: '100%',
    height: 2,
    backgroundColor: colors.white,
    borderRadius: 1,
    ...(isWeb ? { transition: 'all 0.3s ease' } : {}),
  },
  menuLineActive1: {
    transform: [{ rotate: '45deg' }, { translateY: 11 }],
    backgroundColor: colors.gold,
  },
  menuLineActive2: {
    opacity: 0,
  },
  menuLineActive3: {
    transform: [{ rotate: '-45deg' }, { translateY: -11 }],
    backgroundColor: colors.gold,
  },
  
  // Full Screen Mobile Menu Overlay
  mobileOverlayContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 99999,
  },
  mobileOverlayBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  mobileOverlayPanel: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100%',
    maxWidth: 400,
    height: '100%',
    backgroundColor: colors.darkNavy,
    borderLeftWidth: 1,
    borderLeftColor: colors.glassBorder,
  },
  mobileOverlayClose: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.teal + '30',
    zIndex: 10,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  mobileOverlayCloseX: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileOverlayCloseLine: {
    position: 'absolute',
    width: 18,
    height: 2,
    backgroundColor: colors.teal,
    borderRadius: 1,
  },
  mobileOverlayScroll: {
    flex: 1,
  },
  mobileOverlayScrollContent: {
    paddingTop: 80,
    paddingHorizontal: 24,
    paddingBottom: 40,
    minHeight: '100%',
  },
  mobileOverlayLogo: {
    alignItems: 'center',
    marginBottom: 32,
  },
  mobileOverlayLogoText: {
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: 1,
  },
  mobileOverlayLogoBar: {
    width: 60,
    height: 3,
    backgroundColor: colors.teal,
    marginTop: 12,
    borderRadius: 2,
  },
  mobileOverlayNav: {
    marginBottom: 24,
  },
  mobileOverlayItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 18,
    paddingHorizontal: 20,
    marginBottom: 10,
    borderRadius: 12,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { 
      transition: 'all 0.15s ease',
      cursor: 'pointer',
    } : {}),
  },
  mobileOverlayItemAdmin: {
    backgroundColor: colors.teal + '15',
    borderColor: colors.teal + '40',
    marginTop: 8,
  },
  mobileOverlayItemText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '600',
    flex: 1,
  },
  mobileOverlayItemArrow: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '600',
    marginRight: 14,
  },
  mobileOverlayItemArrowEnd: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '600',
  },
  mobileOverlayCTAContainer: {
    marginBottom: 32,
  },
  mobileOverlayCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 18,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 10,
    ...(isWeb ? { 
      boxShadow: '0 4px 20px rgba(212, 168, 75, 0.3)',
      cursor: 'pointer',
    } : {}),
  },
  mobileOverlayCTAText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },
  mobileOverlayCTAArrow: {
    color: colors.darkNavy,
    fontSize: 18,
    fontWeight: '700',
  },
  mobileOverlayFooter: {
    alignItems: 'center',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  mobileOverlayPhoneBtn: {
    marginBottom: 8,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  mobileOverlayPhone: {
    color: colors.teal,
    fontSize: 18,
    fontWeight: '700',
  },
  mobileOverlayHours: {
    color: colors.gray,
    fontSize: 14,
    marginBottom: 12,
  },
  mobileOverlaySocialText: {
    color: colors.grayDark,
    fontSize: 11,
    letterSpacing: 1.5,
  },

  // ========== HERO ==========
  hero: {
    minHeight: height,
    paddingTop: 80,
    paddingHorizontal: 24,
    paddingBottom: 40,
    position: 'relative',
    justifyContent: 'center', // Center content vertically
  },
  heroBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  heroBgOrb1: {
    position: 'absolute',
    width: 700,
    height: 700,
    borderRadius: 350,
    backgroundColor: colors.tealGlow,
    top: -200,
    right: -200,
    ...(isWeb ? { filter: 'blur(120px)' } : {}),
  },
  heroBgOrb2: {
    position: 'absolute',
    width: 500,
    height: 500,
    borderRadius: 250,
    backgroundColor: 'rgba(255, 215, 0, 0.08)',
    bottom: -100,
    left: -150,
    ...(isWeb ? { filter: 'blur(100px)' } : {}),
  },
  heroBgGrid: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
    ...(isWeb ? {
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
      backgroundSize: '60px 60px',
    } : {}),
  },
  heroContent: {
    flexDirection: isWeb && width > 1000 ? 'row' : 'column',
    maxWidth: 1400,
    width: '100%',
    alignSelf: 'center',
    gap: isWeb && width > 1000 ? 80 : 60,
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1,
  },
  heroLeft: {
    flex: 1,
    maxWidth: 600,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 100,
    alignSelf: 'flex-start',
    marginBottom: 28,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    position: 'relative',
  },
  heroBadgePulse: {
    position: 'absolute',
    left: 16,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.teal,
    opacity: 0.3,
  },
  heroBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.teal,
    marginRight: 12,
  },
  heroBadgeText: {
    color: colors.grayLight,
    fontSize: 13,
    fontWeight: '500',
  },
  heroTitle: {
    fontSize: isWeb && width > 768 ? 88 : 56,
    fontWeight: '800',
    color: colors.white,
    lineHeight: isWeb && width > 768 ? 98 : 64,
    marginBottom: 28,
    letterSpacing: -2,
  },
  heroTitleGradient: {
    color: colors.teal,
  },
  heroSubtitle: {
    fontSize: isWeb && width > 768 ? 24 : 20,
    color: colors.gray,
    lineHeight: isWeb && width > 768 ? 40 : 34,
    marginBottom: 36,
  },
  heroSubtitleAccent: {
    color: colors.grayLight,
    fontWeight: '600',
  },
  trustRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 36,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  trustIcon: {
    color: colors.teal,
    fontSize: 18,
    fontWeight: '700',
  },
  trustText: {
    color: colors.grayLight,
    fontSize: 16,
    fontWeight: '500',
  },
  heroStats: {
    flexDirection: 'row',
    gap: 44,
    marginBottom: 48,
  },
  heroStatItem: {
    alignItems: 'center',
  },
  heroStatNumber: {
    fontSize: 42,
    fontWeight: '800',
    color: colors.white,
    marginBottom: 4,
  },
  heroStatLabel: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  heroCTAs: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  heroPrimaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 18,
    paddingHorizontal: 32,
    borderRadius: 12,
    gap: 10,
    position: 'relative',
    overflow: 'hidden',
  },
  heroPrimaryBtnGlow: {
    position: 'absolute',
    top: -30,
    right: -30,
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  heroPrimaryBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },
  heroPrimaryBtnArrow: {
    color: colors.darkNavy,
    fontSize: 18,
    fontWeight: '600',
  },
  heroSecondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 18,
    paddingHorizontal: 24,
    gap: 14,
  },
  playIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
  },
  playIcon: {
    color: colors.teal,
    fontSize: 14,
    marginLeft: 3,
  },
  heroSecondaryBtnText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
  },

  // ========== ESTIMATE CARD ==========
  heroRight: {
    flex: isWeb && width > 1000 ? 0.85 : 1,
    width: '100%',
    maxWidth: 460,
    alignSelf: isWeb && width > 1000 ? 'flex-start' : 'center',
    marginLeft: isWeb && width > 1000 ? 'auto' : 0,
    marginRight: isWeb && width > 1000 ? 0 : 0,
  },
  estimateCard: {
    backgroundColor: colors.navy,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    position: 'relative',
    overflow: 'hidden',
    minHeight: isWeb && width > 1000 ? 520 : 'auto', // Taller to match left side
    ...(isWeb ? { 
      boxShadow: '0 30px 60px rgba(0,0,0,0.4)',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    } : {}),
  },
  estimateCardHighlight: {
    borderColor: '#d4a84b',
    ...(isWeb ? { 
      boxShadow: '0 0 30px rgba(212, 168, 75, 0.5), 0 30px 60px rgba(0,0,0,0.4)',
    } : {}),
  },
  estimateCardGlow: {
    position: 'absolute',
    top: -100,
    right: -100,
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: colors.tealGlow,
    ...(isWeb ? { filter: 'blur(60px)' } : {}),
  },
  estimateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  estimateDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.teal,
    marginRight: 12,
  },
  estimateTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
  },
  estimateSubtitle: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 28,
  },
  formField: {
    marginBottom: 18,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 10,
  },
  formRow: {
    flexDirection: 'row',
  },
  formInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 18,
    fontSize: 15,
    color: colors.white,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  formTextarea: {
    height: 90,
    textAlignVertical: 'top',
    paddingTop: 16,
  },
  formTextareaBig: {
    height: 160,
    textAlignVertical: 'top',
    paddingTop: 16,
  },
  formSubmit: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 18,
    borderRadius: 12,
    gap: 10,
    marginTop: 8,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: `0 4px 20px ${colors.teal}40, 0 0 40px ${colors.teal}20`,
    transform: [{ scale: 1 }],
  },
  formSubmitText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  formSubmitArrow: {
    color: colors.darkNavy,
    fontSize: 18,
  },
  formSubmitDisabled: {
    opacity: 0.85, // Subtle indication, not dead-looking
  },
  formSubmitLoading: {
    backgroundColor: colors.tealDark,
  },
  formLabelHint: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 10,
    fontStyle: 'italic',
  },
  loadingSpinner: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: colors.darkNavy,
    borderTopColor: 'transparent',
    marginRight: 10,
  },
  backBtn: {
    marginRight: 12,
  },
  backBtnText: {
    color: colors.gray,
    fontSize: 14,
  },
  
  // Step Indicator
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepDot: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepDotActive: {
    backgroundColor: colors.teal,
  },
  stepDotDone: {
    backgroundColor: colors.teal,
  },
  stepDotCheck: {
    color: colors.darkNavy,
    fontSize: 12,
    fontWeight: '700',
  },
  stepDotNum: {
    color: colors.darkNavy,
    fontSize: 12,
    fontWeight: '700',
  },
  stepDotNumInactive: {
    color: colors.gray,
    fontSize: 12,
    fontWeight: '600',
  },
  stepLine: {
    width: 40,
    height: 2,
    backgroundColor: colors.navyMid,
    marginHorizontal: 4,
  },
  stepLineDone: {
    backgroundColor: colors.teal,
  },

  // Zipcode Status
  zipcodeStatus: {
    marginTop: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  zipcodeStatusGood: {
    backgroundColor: colors.teal + '15',
  },
  zipcodeStatusBad: {
    backgroundColor: '#f85149' + '15',
  },
  zipcodeStatusText: {
    color: colors.grayLight,
    fontSize: 13,
    fontWeight: '500',
  },
  zipcodeStatusFee: {
    color: colors.gold,
    fontSize: 12,
    fontWeight: '600',
  },

  // AI Hint
  aiHint: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 20,
  },
  aiHintIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  aiHintText: {
    flex: 1,
    fontSize: 12,
    color: colors.gray,
    lineHeight: 18,
  },

  // Quote Range
  quoteRange: {
    backgroundColor: colors.teal + '15',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  quoteRangeLabel: {
    fontSize: 12,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  quoteRangeValue: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.teal,
    marginBottom: 4,
  },
  quoteRangeNote: {
    fontSize: 11,
    color: colors.gray,
  },

  // Quote Breakdown
  quoteBreakdown: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  quoteBreakdownTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  breakdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  breakdownItemMain: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  breakdownItemName: {
    fontSize: 14,
    color: colors.gray,
  },
  breakdownItemQty: {
    fontSize: 12,
    color: colors.teal,
    marginLeft: 8,
  },
  breakdownItemPrice: {
    fontSize: 14,
    color: colors.grayLight,
    fontWeight: '600',
  },
  breakdownDivider: {
    height: 1,
    backgroundColor: colors.navyMid,
    marginVertical: 8,
  },
  breakdownTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  breakdownTotalLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.white,
  },
  breakdownTotalValue: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.teal,
  },

  // Quote Details
  quoteDetails: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  quoteDetailItem: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 14,
    alignItems: 'center',
  },
  quoteDetailIcon: {
    fontSize: 18,
    marginBottom: 6,
  },
  quoteDetailLabel: {
    fontSize: 11,
    color: colors.gray,
    marginBottom: 4,
  },
  quoteDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    textTransform: 'capitalize',
  },

  // Quote Notes
  quoteNotes: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.gold + '10',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.gold + '20',
  },
  quoteNotesIcon: {
    fontSize: 16,
    marginRight: 10,
    marginTop: 2,
  },
  quoteNotesText: {
    flex: 1,
    fontSize: 13,
    color: colors.grayLight,
    lineHeight: 20,
  },

  // Out of Scope
  outOfScope: {
    backgroundColor: '#f85149' + '15',
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#f85149' + '30',
  },
  outOfScopeIcon: {
    fontSize: 32,
    marginBottom: 12,
  },
  outOfScopeTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 8,
    textAlign: 'center',
  },
  outOfScopeText: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 22,
  },
  outOfScopeAction: {
    fontSize: 13,
    color: colors.grayLight,
    fontWeight: '500',
  },

  // Quote CTAs
  quoteCTAs: {
    gap: 12,
  },
  quoteSecondaryBtn: {
    paddingVertical: 14,
    alignItems: 'center',
  },
  quoteSecondaryBtnText: {
    color: colors.gray,
    fontSize: 14,
    fontWeight: '500',
  },

  // Booking/Scheduling Styles
  bookingSummary: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  bookingSummaryTitle: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  bookingSummaryTextSmall: {
    color: colors.gray,
    fontSize: 12,
    marginBottom: 8,
  },
  bookingSummaryEstimate: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '700',
    marginTop: 4,
  },
  slotsContainer: {
    maxHeight: 200,
    marginBottom: 12,
  },
  slotDay: {
    marginBottom: 16,
  },
  slotDayTitle: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 10,
  },
  slotTimeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  slotTimeBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  slotTimeBtnSelected: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  slotTimeText: {
    color: colors.grayLight,
    fontSize: 12,
    fontWeight: '500',
  },
  slotTimeTextSelected: {
    color: colors.darkNavy,
    fontWeight: '700',
  },
  noSlotsMessage: {
    padding: 24,
    alignItems: 'center',
  },
  noSlotsText: {
    color: colors.gray,
    fontSize: 13,
    textAlign: 'center',
  },
  selectedSlotBanner: {
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  selectedSlotText: {
    color: colors.teal,
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  confirmationContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  confirmationIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  confirmationIconText: {
    color: colors.darkNavy,
    fontSize: 28,
    fontWeight: '700',
  },
  confirmationTitle: {
    color: colors.white,
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 4,
  },
  confirmationSubtitle: {
    color: colors.gray,
    fontSize: 14,
    marginBottom: 20,
  },
  confirmationDetails: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    width: '100%',
    marginBottom: 16,
    gap: 8,
  },
  confirmationValue: {
    color: colors.grayLight,
    fontSize: 14,
  },
  confirmationNote: {
    backgroundColor: colors.teal + '10',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    width: '100%',
  },
  confirmationNoteText: {
    color: colors.teal,
    fontSize: 12,
    textAlign: 'center',
  },

  formNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 18,
    gap: 8,
  },
  formNoteIcon: {
    fontSize: 14,
  },
  formNoteText: {
    fontSize: 13,
    color: colors.gray,
  },
  heroCallBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    marginTop: 16,
    gap: 12,
    alignSelf: 'center',
    ...(isWeb ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {}),
  },
  heroCallIcon: {
    fontSize: 18,
    color: colors.teal,
  },
  heroCallContent: {
    flexDirection: 'column',
  },
  heroCallLabel: {
    fontSize: 11,
    color: colors.gray,
    marginBottom: 2,
  },
  heroCallNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.teal,
  },
  heroCallArrow: {
    fontSize: 18,
    color: colors.teal,
  },

  // ========== SCROLL INDICATOR ==========
  scrollIndicator: {
    position: 'absolute',
    bottom: 40,
    left: '50%',
    transform: [{ translateX: -40 }],
    alignItems: 'center',
    opacity: 0.6,
    display: isWeb && width < 768 ? 'none' : 'flex', // Hide on mobile to avoid overlap with call button
  },
  scrollText: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  scrollLine: {
    width: 1,
    height: 40,
    backgroundColor: colors.navyMid,
    overflow: 'hidden',
  },
  scrollDot: {
    width: 3,
    height: 8,
    borderRadius: 1.5,
    backgroundColor: colors.teal,
    marginLeft: -1,
  },

  // ========== MARQUEE ==========
  marquee: {
    backgroundColor: colors.navyLight,
    paddingVertical: 18,
    overflow: 'hidden',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.glassBorder,
  },
  marqueeTrack: {
    flexDirection: 'row',
  },
  marqueeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 28,
  },
  marqueeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.teal,
    marginRight: 14,
  },
  marqueeText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.grayLight,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },

  // ========== SECTION HEADERS ==========
  sectionHeader: {
    alignItems: 'center',
    marginBottom: 60,
    maxWidth: 600,
    alignSelf: 'center',
  },
  sectionTag: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.teal,
    letterSpacing: 3,
    textTransform: 'uppercase',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: isWeb && width > 768 ? 48 : 34,
    fontWeight: '800',
    color: colors.white,
    textAlign: 'center',
    lineHeight: isWeb && width > 768 ? 58 : 44,
    letterSpacing: -0.5,
    marginBottom: 16,
  },
  sectionTitleAccent: {
    color: colors.teal,
  },
  sectionDesc: {
    fontSize: 16,
    color: colors.gray,
    textAlign: 'center',
    lineHeight: 26,
  },

  // ========== SERVICES ==========
  servicesSection: {
    paddingVertical: 100,
    paddingHorizontal: 24,
    backgroundColor: colors.darkNavy,
  },
  servicesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: isWeb && width < 600 ? 12 : 20,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  serviceCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 0,
    width: isWeb && width > 1000 ? 'calc(33.333% - 14px)' : isWeb && width > 600 ? 'calc(50% - 10px)' : 'calc(50% - 8px)',
    minWidth: isWeb && width < 600 ? 'auto' : 200,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    position: 'relative',
    overflow: 'hidden',
    ...(isWeb ? { 
      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)', 
      cursor: 'pointer',
      transformStyle: 'preserve-3d',
    } : {}),
  },
  serviceCardHovered: {
    transform: [{ translateY: -5 }],
    borderColor: colors.teal + '50',
  },
  flipCardContainer: {
    position: 'relative',
    width: isWeb && width > 1000 ? 'calc(33.333% - 14px)' : isWeb && width > 600 ? 'calc(50% - 10px)' : 'calc(50% - 8px)',
    minWidth: isWeb && width < 600 ? 'auto' : 200,
    height: isWeb && width < 600 ? 180 : 220,
    ...(isWeb ? { 
      perspective: '1000px',
    } : {}),
  },
  flipCardFace: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    ...(isWeb ? { 
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
      transformStyle: 'preserve-3d',
      transition: 'transform 0.4s ease-in-out',
    } : {}),
  },
  flipCardTouchable: {
    flex: 1,
    padding: isWeb && width < 600 ? 16 : 20,
  },
  serviceCardBack: {
    justifyContent: 'flex-start',
  },
  serviceBackHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingBottom: 8,
    marginBottom: 8,
    borderBottomWidth: 1,
  },
  serviceBackTitle: {
    fontSize: isWeb && width < 600 ? 13 : 15,
    fontWeight: '700',
  },
  serviceBackDesc: {
    fontSize: isWeb && width < 600 ? 11 : 12,
    color: colors.grayLight,
    lineHeight: isWeb && width < 600 ? 16 : 18,
    marginBottom: 12,
    flex: 1,
  },
  serviceGetEstimateBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  serviceGetEstimateBtnText: {
    color: colors.darkNavy,
    fontSize: isWeb && width < 600 ? 12 : 13,
    fontWeight: '700',
  },
  tapToFlipBack: {
    textAlign: 'center',
    color: colors.gray,
    fontSize: 10,
    marginTop: 6,
  },
  serviceIconBg: {
    width: isWeb && width < 600 ? 44 : 52,
    height: isWeb && width < 600 ? 44 : 52,
    borderRadius: isWeb && width < 600 ? 10 : 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: isWeb && width < 600 ? 12 : 16,
  },
  serviceIcon: {
    fontSize: isWeb && width < 600 ? 20 : 24,
  },
  serviceTitle: {
    fontSize: isWeb && width < 600 ? 14 : 16,
    fontWeight: '700',
    color: colors.white,
    marginBottom: isWeb && width < 600 ? 4 : 6,
  },
  serviceDesc: {
    fontSize: isWeb && width < 600 ? 11 : 13,
    color: colors.gray,
    lineHeight: isWeb && width < 600 ? 16 : 19,
  },
  serviceArrow: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  serviceArrowText: {
    fontSize: 16,
    fontWeight: '600',
  },
  serviceAccent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
  },
  servicesCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 50,
    gap: 12,
    backgroundColor: colors.teal,
    paddingVertical: 18,
    paddingHorizontal: 32,
    borderRadius: 12,
    alignSelf: 'center',
    ...(isWeb ? { 
      cursor: 'pointer',
      boxShadow: '0 8px 30px rgba(212, 168, 75, 0.3)',
      transition: 'all 0.3s ease',
    } : {}),
  },
  servicesCTAText: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  servicesCTAArrow: {
    fontSize: 20,
    color: colors.darkNavy,
  },

  // ========== PORTFOLIO ==========
  portfolioSection: {
    paddingVertical: 100,
    paddingHorizontal: 24,
    backgroundColor: colors.navy,
  },
  filterBar: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 40,
    flexWrap: 'wrap',
  },
  filterBtn: {
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 100,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  filterBtnActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  filterBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.grayLight,
  },
  filterBtnTextActive: {
    color: colors.darkNavy,
  },
  portfolioGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  portfolioCard: {
    width: isWeb && width > 768 ? 'calc(33.333% - 14px)' : '100%',
    aspectRatio: 1.3,
    borderRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  portfolioCardLarge: {
    width: isWeb && width > 768 ? 'calc(50% - 10px)' : '100%',
    aspectRatio: 1.6,
  },
  portfolioCardBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  portfolioCardIcon: {
    fontSize: 60,
    opacity: 0.25,
    color: colors.white,
  },
  portfolioOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
    ...(isWeb ? { backgroundImage: 'linear-gradient(transparent, rgba(0,0,0,0.85))' } : {}),
  },
  portfolioCategory: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
    color: colors.teal, // Fallback color
  },
  portfolioTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  playBadge: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  playBadgeIcon: {
    color: colors.white,
    fontSize: 16,
    marginLeft: 3,
  },
  portfolioCardImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  portfolioLightbox: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  portfolioLightboxClose: {
    position: 'absolute',
    top: 40,
    right: 30,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  portfolioLightboxCloseText: {
    color: colors.white,
    fontSize: 24,
    fontWeight: '300',
  },
  portfolioLightboxContent: {
    width: '90%',
    maxWidth: 900,
    aspectRatio: 16/9,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.navy,
  },
  portfolioLightboxImage: {
    width: '100%',
    height: '100%',
  },
  portfolioLightboxText: {
    color: colors.white,
    fontSize: 18,
    textAlign: 'center',
    padding: 40,
  },
  portfolioLightboxTitle: {
    color: colors.white,
    fontSize: 22,
    fontWeight: '700',
    marginTop: 20,
    textAlign: 'center',
  },
  portfolioLightboxDesc: {
    color: colors.gray,
    fontSize: 15,
    marginTop: 8,
    textAlign: 'center',
    maxWidth: 600,
  },

  // ========== ABOUT ==========
  aboutSection: {
    paddingVertical: 100,
    paddingHorizontal: 24,
    paddingBottom: isWeb && width < 768 ? 80 : 100, // Extra space before CTA on mobile
    backgroundColor: colors.darkNavy,
    overflow: 'visible', // Ensure content isn't clipped
  },
  aboutContent: {
    flexDirection: isWeb && width > 900 ? 'row' : 'column',
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    gap: 60,
    alignItems: 'center',
  },
  aboutLeft: {
    flex: 1,
  },
  aboutImageWrap: {
    width: isWeb && width > 768 ? 400 : 300,
    height: isWeb && width > 768 ? 450 : 350,
    backgroundColor: colors.navyLight,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  aboutImage: {
    width: '90%',
    height: '90%',
    opacity: 0.9,
  },
  aboutBadge: {
    position: 'absolute',
    bottom: -20,
    right: -20,
    backgroundColor: colors.teal,
    paddingVertical: 20,
    paddingHorizontal: 28,
    borderRadius: 16,
  },
  aboutBadgeNumber: {
    fontSize: 36,
    fontWeight: '800',
    color: colors.darkNavy,
  },
  aboutBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  aboutRight: {
    flex: 1,
    paddingBottom: isWeb && width < 768 ? 20 : 0, // Extra padding on mobile
  },
  aboutTitle: {
    fontSize: isWeb && width > 768 ? 42 : 32,
    fontWeight: '800',
    color: colors.white,
    lineHeight: isWeb && width > 768 ? 52 : 42,
    marginTop: 16,
    marginBottom: 20,
    letterSpacing: -0.5,
  },
  aboutTitleAccent: {
    color: colors.teal,
  },
  aboutDesc: {
    fontSize: 16,
    color: colors.gray,
    lineHeight: 28,
    marginBottom: 32,
  },
  aboutFeatures: {
    gap: 18,
    marginBottom: 36,
  },
  aboutFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  aboutFeatureCheck: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  aboutFeatureCheckIcon: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '700',
  },
  aboutFeatureText: {
    fontSize: 15,
    color: colors.grayLight,
    fontWeight: '500',
  },
  aboutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignSelf: 'flex-start',
    gap: 10,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    marginBottom: 20, // Ensure button doesn't get clipped
  },
  aboutBtnText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
  aboutBtnArrow: {
    color: colors.teal,
    fontSize: 18,
  },

  // ========== TESTIMONIALS CAROUSEL ==========
  testimonialsSection: {
    paddingVertical: 100,
    paddingHorizontal: 0,
    backgroundColor: colors.navy,
    overflow: 'hidden',
  },
  testimonialCount: {
    fontSize: 14,
    color: colors.gray,
    marginTop: 12,
    textAlign: 'center',
  },
  carouselContainer: {
    position: 'relative',
    width: '100%',
    marginTop: 30,
    overflow: 'visible',
    // No maxWidth - carousel bleeds to screen edges
  },
  carouselTrack: {
    paddingVertical: 20,
  },
  carouselArrow: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -25 }],
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.darkNavy,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { 
      cursor: 'pointer', 
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    } : {}),
  },
  carouselArrowLeft: {
    left: isWeb && width > 768 ? 40 : 10,
  },
  carouselArrowRight: {
    right: isWeb && width > 768 ? 40 : 10,
  },
  carouselArrowText: {
    fontSize: 32,
    color: colors.teal,
    fontWeight: '300',
    marginTop: -4,
  },
  testimonialCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 20,
    padding: 28,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    position: 'relative',
    minHeight: 280,
    ...(isWeb ? { 
      transition: 'all 0.3s ease',
    } : {}),
  },
  testimonialQuote: {
    position: 'absolute',
    top: 20,
    right: 28,
    fontSize: 70,
    color: colors.teal,
    opacity: 0.2,
    fontFamily: 'serif',
  },
  testimonialStars: {
    flexDirection: 'row',
    marginBottom: 18,
  },
  star: {
    fontSize: 18,
    color: colors.gold,
    marginRight: 3,
  },
  testimonialText: {
    fontSize: 15,
    color: colors.grayLight,
    lineHeight: 26,
    marginBottom: 28,
  },
  testimonialAuthor: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  authorAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
  },
  authorInitial: {
    color: colors.darkNavy,
    fontSize: 20,
    fontWeight: '700',
  },
  authorName: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
  authorLocation: {
    fontSize: 13,
    color: colors.gray,
  },
  testimonialDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    marginTop: 40,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.navyMid,
  },
  dotActive: {
    backgroundColor: colors.teal,
    width: 30,
  },
  swipeHint: {
    textAlign: 'center',
    color: colors.gray,
    fontSize: 13,
    marginTop: 20,
    fontWeight: '500',
  },

  // ========== CTA ==========
  ctaSection: {
    paddingVertical: 80,
    paddingHorizontal: 24,
    backgroundColor: colors.teal,
    position: 'relative',
    overflow: 'hidden',
  },
  ctaGlow: {
    position: 'absolute',
    top: -100,
    left: '50%',
    width: 400,
    height: 200,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 200,
    ...(isWeb ? { filter: 'blur(60px)' } : {}),
  },
  ctaContent: {
    maxWidth: 700,
    alignSelf: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  ctaTitle: {
    fontSize: isWeb && width > 768 ? 44 : 32,
    fontWeight: '800',
    color: colors.darkNavy,
    textAlign: 'center',
    marginBottom: 16,
  },
  ctaSubtitle: {
    fontSize: 17,
    color: colors.darkNavy,
    textAlign: 'center',
    marginBottom: 36,
    opacity: 0.8,
  },
  ctaButtons: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  ctaPrimaryBtn: {
    backgroundColor: colors.darkNavy,
    paddingVertical: 18,
    paddingHorizontal: 32,
    borderRadius: 12,
  },
  ctaPrimaryBtnText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  ctaSecondaryBtn: {
    backgroundColor: 'transparent',
    paddingVertical: 18,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.darkNavy,
  },
  ctaSecondaryBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },

  // ========== FOOTER ==========
  footer: {
    backgroundColor: colors.darkNavy,
    paddingTop: 80,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  footerTop: {
    flexDirection: isWeb && width > 900 ? 'row' : 'column',
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    gap: 60,
    paddingBottom: 60,
  },
  footerBrand: {
    flex: 1.5,
    maxWidth: 320,
  },
  footerLogoWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  footerLogo: {
    width: 40,
    height: 40,
    borderRadius: 10,
  },
  footerLogoText: {
    fontSize: 17,
    fontWeight: '800',
    color: colors.white,
    marginLeft: 12,
  },
  footerLogoAccent: {
    color: colors.teal,
  },
  footerDesc: {
    fontSize: 14,
    color: colors.gray,
    lineHeight: 24,
    marginBottom: 24,
  },
  socialLinks: {
    flexDirection: 'row',
    gap: 12,
  },
  socialBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  socialIcon: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '700',
  },
  footerLinks: {
    flex: 2,
    flexDirection: 'row',
    gap: 50,
    flexWrap: 'wrap',
  },
  footerCol: {
    minWidth: 140,
  },
  footerColTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 20,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  footerLink: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 14,
  },
  footerContact: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 14,
  },
  footerBottom: {
    flexDirection: isWeb && width > 600 ? 'row' : 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
    paddingVertical: 24,
    gap: 16,
  },
  copyright: {
    fontSize: 13,
    color: colors.gray,
  },
  footerLegal: {
    flexDirection: 'row',
    gap: 8,
  },
  legalLink: {
    fontSize: 13,
    color: colors.gray,
  },
  legalDivider: {
    color: colors.grayDark,
  },
});
